import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import {
  Trophy, Calendar, MapPin, Phone, Award, ChevronRight, ChevronLeft,
  Ticket, ShoppingBag, ArrowRight, ArrowDown, Mail, Facebook, Instagram, Twitter,
  Star, Flame, Play, Eye, Plane, Home, Briefcase, Users as UsersIcon, GraduationCap,
  Heart, HandHeart, Navigation, Megaphone, Target, X,
} from "lucide-react";
import { api, API } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { TornEdge } from "../../components/sport/TornEdge";
import SocialLinks from "../../components/sport/SocialLinks";
import { LnhBadge } from "../../components/sport/LnhBadge";
import { MatchCountdown } from "../../components/sport/MatchCountdown";
import MatchPronoModal from "../../components/sport/MatchPronoModal";
import { findResumeVideo, videoWatchUrl } from "../../lib/matchVideos";
import SportHeader from "../../components/sport/SportHeader";
import SportSpeedDial from "../../components/sport/SportSpeedDial";
import ParallaxBull from "../../components/sport/ParallaxBull";
import CartDrawer from "../../components/public/CartDrawer";
import FloatingInstallPWA from "../../components/sport/FloatingInstallPWA";

// ======================================================================
// Helpers
// ======================================================================
const PRIMARY = "#4F1B7C";      // vibrant violet (matches official IPH brand)
const SECONDARY = "#EC1F7C";    // hot pink — accent color
const BG_DARK = "#160732";
const BG_DEEP = "#280F4F";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.07, ease: [0.21, 0.94, 0.4, 1] },
  }),
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "long" });
  } catch { return iso; }
}

// Combine match date_iso + time into a real Date for the countdown.
// `time` may be "20:00" or "20h00" or absent.
function kickoffDate(match) {
  if (!match) return null;
  const iso = match.date_iso || match.date || "";
  if (!iso) return null;
  // If iso already includes a T (full datetime), use it directly.
  if (typeof iso === "string" && iso.includes("T")) {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  }
  const t = (match.time || "").toString().replace("h", ":").replace(/[^0-9:]/g, "");
  const [hh = "20", mm = "00"] = t.split(":");
  // Build local datetime in France TZ implicitly (browser local). For users
  // in other timezones the LNH ISO date is the source of truth.
  const d = new Date(`${iso}T${hh.padStart(2, "0")}:${mm.padStart(2, "0")}:00`);
  return isNaN(d.getTime()) ? null : d;
}

// Robust team-code comparison: LNH returns different conventions across endpoints
// (e.g. standings → "istres", calendar → "ist", page slug → "istres-provence-handball").
// Normalise both sides by taking the lowercase first 3 chars OR using startsWith on either side.
function sameTeam(a, b) {
  const x = (a || "").toString().toLowerCase().trim();
  const y = (b || "").toString().toLowerCase().trim();
  if (!x || !y) return false;
  if (x === y) return true;
  return x.startsWith(y) || y.startsWith(x);
}

function ourTeamCode(settings) {
  return (settings?.team_code || "ist").toLowerCase();
}

// Animated Play icon — soft pulsing halo + subtle scale to attract clicks.
// `subtle` reduces the pulse for tight UIs (e.g. small round buttons in lists).
function AnimatedPlay({ size = 14, subtle = false }) {
  return (
    <span
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {/* halo ping */}
      <span
        className={`absolute inset-0 rounded-full ${subtle ? "opacity-50" : "opacity-80"} animate-ping`}
        style={{ background: "currentColor", animationDuration: subtle ? "2.6s" : "1.8s" }}
      />
      {/* pulsing icon */}
      <motion.span
        className="relative inline-flex items-center justify-center"
        animate={subtle ? { scale: [1, 1.08, 1] } : { scale: [1, 1.18, 1] }}
        transition={{ duration: subtle ? 2.2 : 1.4, repeat: Infinity, ease: "easeInOut" }}
      >
        <Play size={size} fill="currentColor"/>
      </motion.span>
    </span>
  );
}

// Returns the LNH club page URL for a given team code/slug.
// Pattern: https://www.lnh.fr/liquimoly-starligue/clubs/{slug}
const LNH_CLUB_BASE = "https://www.lnh.fr/liquimoly-starligue/clubs";
function lnhClubUrl(code, fallbackName = "") {
  const slug = (code || fallbackName || "").toString().toLowerCase().trim()
    .replace(/[àâä]/g, "a").replace(/[éèêë]/g, "e").replace(/[îï]/g, "i")
    .replace(/[ôö]/g, "o").replace(/[ùûü]/g, "u").replace(/[ç]/g, "c")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  if (!slug) return LNH_CLUB_BASE;
  return `${LNH_CLUB_BASE}/${slug}`;
}

// Wrap a club logo (or any element) into an external link to its LNH page.
// Used for opposing teams in standings, calendar, match cards, etc.
function ClubLink({ code, name, ourCode, className = "", children, title }) {
  const isUs = sameTeam(code, ourCode);
  const url = lnhClubUrl(code, name);
  // Don't link to LNH for OUR own team — keep behaviour subtle
  if (isUs) {
    return <span className={className} title={title || name}>{children}</span>;
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className={`${className} hover:opacity-80 transition`}
      title={`Voir ${name || code} sur LNH.fr`}
      data-testid={`lnh-club-link-${code || name}`}>
      {children}
    </a>
  );
}

// Group player position into broader buckets
function positionBucket(p = "") {
  const s = (p || "").toLowerCase();
  if (s.includes("gard")) return "gardiens";
  if (s.includes("demi")) return "demi-centres";
  if (s.includes("ailier") && s.includes("gauche")) return "ailiers-gauche";
  if (s.includes("ailier") && s.includes("droit")) return "ailiers-droit";
  if (s.includes("arri") && s.includes("gauche")) return "arrieres-gauche";
  if (s.includes("arri") && s.includes("droit")) return "arrieres-droit";
  if (s.includes("pivot")) return "pivots";
  return "autres";
}

const POSITION_GROUPS = [
  { key: "gardiens", label: "Gardiens" },
  { key: "ailiers-gauche", label: "Ailiers Gauche" },
  { key: "arrieres-gauche", label: "Arrières Gauche" },
  { key: "demi-centres", label: "Demi-Centres" },
  { key: "arrieres-droit", label: "Arrières Droit" },
  { key: "ailiers-droit", label: "Ailiers Droit" },
  { key: "pivots", label: "Pivots" },
  { key: "autres", label: "Autres" },
];

// ======================================================================
// HERO SECTION
// ======================================================================
const BRUSH_TITLE_URL = "https://customer-assets.emergentagent.com/job_pod-istres-hb/artifacts/4xvd9jh9_Terre%20de%20handball-blanc-logo.png";
const BALL_URL = "https://customer-assets.emergentagent.com/job_pod-istres-hb/artifacts/qz2qd3a5_shopping-Photoroom.png";

function HeroSection({ settings }) {
  const videoUrl = settings?.hero_video_url;
  const poster = settings?.hero_video_poster;
  const slogan = settings?.slogan || "#istresterredehandball";
  const logo = settings?.logo_url || "https://lnh.fr/medias/sports_teams/istres__logo__2023-2024.png";

  const heroRef = useRef(null);
  const [pointer, setPointer] = useState({ x: 0, y: 0 });

  const onMouseMove = (e) => {
    const rect = heroRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    setPointer({ x: (e.clientX - cx) / rect.width, y: (e.clientY - cy) / rect.height });
  };

  return (
    <section
      ref={heroRef}
      onMouseMove={onMouseMove}
      className="relative min-h-svh sm:min-h-screen flex items-center justify-center overflow-hidden hero-landscape-fit"
      style={{ background: BG_DARK }}
    >
      {/* Video / Poster */}
      <div className="absolute inset-0 z-0">
        {videoUrl ? (
          <iframe
            title="hero-video"
            src={videoUrl}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full pointer-events-none"
            allow="autoplay; encrypted-media"
            frameBorder="0"
            loading="lazy"
          />
        ) : poster ? (
          <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : null}
        {/* dark gradient overlay */}
        <div className="absolute inset-0" style={{
          background: `radial-gradient(ellipse at center, rgba(10,4,16,0.45) 0%, rgba(10,4,16,0.85) 70%, rgba(10,4,16,0.97) 100%)`,
        }}/>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2240%22 height=%2240%22 viewBox=%220 0 40 40%22><circle cx=%221%22 cy=%221%22 r=%221%22 fill=%22%23ffffff%22 fill-opacity=%220.04%22/></svg>')] opacity-40"/>
      </div>

      {/* Floating ball with parallax — top right (hidden on mobile per user request) */}
      <motion.img
        src={BALL_URL}
        alt=""
        aria-hidden
        className="hidden sm:block absolute pointer-events-none select-none z-[2] sm:top-[8%]"
        style={{
          width: "clamp(140px, 28vw, 380px)",
          right: "-3%",
          filter: "drop-shadow(0 24px 40px rgba(0,0,0,0.65))",
        }}
        animate={{
          x: pointer.x * 50,
          y: [0, 18, 0, -10, 0],
          rotate: [0, 12, 0, -8, 0],
        }}
        transition={{
          x: { type: "spring", stiffness: 60, damping: 14 },
          y: { duration: 8, repeat: Infinity, ease: "easeInOut" },
          rotate: { duration: 10, repeat: Infinity, ease: "easeInOut" },
        }}
      />
      <motion.img
        src={BALL_URL}
        alt=""
        aria-hidden
        className="absolute pointer-events-none select-none z-[2] opacity-70"
        style={{
          width: "clamp(120px, 18vw, 220px)",
          left: "-2%", bottom: "12%",
          filter: "drop-shadow(0 20px 30px rgba(0,0,0,0.7))",
        }}
        animate={{
          x: pointer.x * -35,
          y: [0, -16, 0, 12, 0],
          rotate: [0, -10, 0, 8, 0],
        }}
        transition={{
          x: { type: "spring", stiffness: 50, damping: 14 },
          y: { duration: 9, repeat: Infinity, ease: "easeInOut" },
          rotate: { duration: 12, repeat: Infinity, ease: "easeInOut" },
        }}
      />

      {/* Content — shifted down so text passes behind the centered logo */}
      <div className="relative z-[5] max-w-6xl mx-auto px-6 pt-24 pb-12 sm:pt-40 sm:pb-24 text-center hero-content">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-pink-400 font-black text-xs sm:text-sm tracking-[0.4em] uppercase mb-6"
        >
          {settings?.season || "Saison 2025-2026"} · Liqui Moly StarLigue
        </motion.div>

        {/* Brush-stroke title image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.95, delay: 0.35, ease: [0.21, 0.94, 0.4, 1] }}
          className="relative inline-block"
        >
          <img
            src={BRUSH_TITLE_URL}
            alt="Istres Terre de Handball"
            className="mx-auto"
            style={{
              maxWidth: "min(340px, 50vw)",
              width: "100%",
              filter: "drop-shadow(0 8px 24px rgba(236,31,124,0.45))",
              mixBlendMode: "screen",
            }}
          />
          {/* Animated underline accent */}
          <motion.div
            className="mx-auto mt-2 h-1 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: "min(420px, 60%)" }}
            transition={{ duration: 1.1, delay: 0.9, ease: [0.21, 0.94, 0.4, 1] }}
            style={{ background: `linear-gradient(90deg, transparent, ${SECONDARY}, transparent)` }}
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.85, duration: 0.6 }}
          className="mt-6 text-white/80 text-base sm:text-lg italic font-light tracking-wider"
        >
          <span className="inline-block px-4 py-1.5 rounded-full border border-pink-500/40 backdrop-blur-sm bg-white/5">
            {slogan}
          </span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.6 }}
          className="mt-10 flex flex-wrap justify-center gap-4"
        >
          <a href={settings?.ticketing_url || "https://billetterie.istreshandball.com/"} target="_blank" rel="noopener noreferrer"
            data-testid="hero-cta-billetterie"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-black uppercase text-sm tracking-wider shadow-2xl hover:scale-105 transition"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: BG_DARK }}>
            <Ticket size={18}/> Billetterie
          </a>
          <Link to="/sport/effectif" data-testid="hero-cta-effectif"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-white/40 text-white font-bold uppercase text-sm tracking-wider bg-white/10 backdrop-blur-md hover:bg-white hover:text-[#1a0820] hover:backdrop-blur-none transition shadow-xl">
            L'effectif <ArrowRight size={16}/>
          </Link>
          <Link to="/sport/boutique" data-testid="hero-cta-boutique"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-pink-500/60 text-pink-200 font-bold uppercase text-sm tracking-wider bg-pink-500/15 backdrop-blur-md hover:bg-pink-500 hover:text-[#1a0820] hover:backdrop-blur-none transition shadow-xl">
            <ShoppingBag size={16}/> Boutique
          </Link>
        </motion.div>
      </div>

      {/* scroll indicator */}
      <motion.div
        animate={{ y: [0, 12, 0] }}
        transition={{ duration: 1.6, repeat: Infinity }}
        className="hidden sm:block absolute bottom-20 left-1/2 -translate-x-1/2 z-10 text-white/60"
      >
        <ArrowDown size={28}/>
      </motion.div>

      {/* Torn edge bottom */}
      <TornEdge position="bottom" color={BG_DARK} variant="shred" height={84}/>
    </section>
  );
}

// ======================================================================
// ======================================================================
// CONFETTI BURST — pure CSS/framer-motion confetti shower
// ======================================================================
import Lottie from "lottie-react";
import confettiAnimation from "../../assets/lottie/confetti.json";

function ConfettiBurst({ count = 36 }) {
  // Real Lottie confetti animation looped over the parent. The `count` prop is kept for compat.
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-10" aria-hidden="true">
      <Lottie
        animationData={confettiAnimation}
        loop
        autoplay
        rendererSettings={{ preserveAspectRatio: "xMidYMid slice" }}
        style={{ width: "100%", height: "100%", position: "absolute", inset: 0 }}
      />
    </div>
  );
}

// ======================================================================
// MATCH HIGHLIGHT (Next match + Last result)
// ======================================================================
function MatchCard({ kind = "next", match, settings, videos = [] }) {
  if (!match) {
    return (
      <div className="rounded-2xl bg-white shadow-xl border border-slate-100 p-8 text-center text-slate-400 italic h-full flex items-center justify-center min-h-[280px]">
        {kind === "next" ? "Pas de prochain match programmé" : "Pas de résultat récent"}
      </div>
    );
  }
  const isLive = match.status === "live";
  const finished = match.status === "finished";
  const ourCode = ourTeamCode(settings);
  const homeIsUs = sameTeam(match.home_code, ourCode);
  const result = finished
    ? (match.score_home > match.score_away ? (homeIsUs ? "win" : "loss")
       : match.score_home < match.score_away ? (homeIsUs ? "loss" : "win") : "draw")
    : null;
  const isWin = result === "win";
  const tagBg = finished
    ? (isWin ? "from-emerald-500 to-emerald-600" : result === "loss" ? "from-rose-500 to-rose-600" : "from-slate-400 to-slate-500")
    : "from-pink-500 to-pink-600";

  // Domicile (home) or Extérieur (away) badge
  const isAwayMatch = !homeIsUs && sameTeam(match.away_code, ourCode);
  const isHomeMatch = homeIsUs;

  return (
    <motion.div
      variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
      className="relative rounded-2xl bg-white shadow-xl overflow-hidden border border-slate-100 group hover:-translate-y-1 transition"
      data-testid={`match-card-${kind}-${result || "scheduled"}`}
    >
      {/* Confetti burst on win */}
      {isWin && <ConfettiBurst count={42}/>}

      {/* Header band */}
      <div className={`bg-gradient-to-r ${tagBg} px-5 py-2.5 flex items-center justify-between relative`}>
        <div className="text-white font-black uppercase tracking-widest text-[11px] flex items-center gap-2">
          {kind === "next" ? <Calendar size={13}/> : <Trophy size={13}/>}
          {kind === "next" ? "Prochain match" : isWin ? "Victoire !" : result === "loss" ? "Défaite" : result === "draw" ? "Match nul" : "Dernier résultat"}
        </div>
        <div className="text-white/90 text-[11px] font-semibold">{match.league || "StarLigue"}</div>
      </div>

      {/* Match location badge — flat round icon: Home (Domicile) or Plane (Extérieur) */}
      {(isHomeMatch || isAwayMatch) && (
        <div className="absolute top-12 right-4 z-20" data-testid={isHomeMatch ? "match-domicile" : "match-exterieur"}>
          <div className="relative flex items-center gap-2">
            <span className={`hidden sm:inline-block text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full ring-1 ${
              isHomeMatch
                ? "text-pink-700 bg-pink-50 ring-pink-100"
                : "text-sky-700 bg-sky-50 ring-sky-100"
            }`}>
              {isHomeMatch ? "Domicile" : "Extérieur"}
            </span>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white shadow-xl ring-4 ring-white ${
              isHomeMatch
                ? "shadow-[0_8px_24px_rgba(236,31,124,0.45)]"
                : "shadow-[0_8px_24px_rgba(14,165,233,0.45)]"
            }`}
              style={{
                background: isHomeMatch
                  ? "linear-gradient(135deg, #ec4899, #be185d)"
                  : "linear-gradient(135deg, #38bdf8, #2563eb)"
              }}>
              {isHomeMatch ? <Home size={18}/> : <Plane size={18} className="rotate-[-30deg]"/>}
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="px-5 py-7 sm:px-8 relative z-[1]">
        <div className="flex items-center justify-between gap-4">
          {/* Home */}
          <div className="flex-1 flex flex-col items-center text-center">
            <ClubLink code={match.home_code} name={match.home_team} ourCode={ourCode}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-slate-50 ring-2 ring-slate-100 flex items-center justify-center p-2 group-hover:scale-110 transition relative">
              <img src={match.home_logo} alt="" className="w-full h-full object-contain"/>
            </ClubLink>
            <ClubLink code={match.home_code} name={match.home_team} ourCode={ourCode}
              className="mt-3 font-black text-slate-900 text-sm sm:text-base uppercase">
              {match.home_team}
            </ClubLink>
          </div>

          {/* Score / VS */}
          <div className="px-2 sm:px-4 text-center">
            {finished ? (
              <div className="flex items-center gap-2">
                <span className={`font-bebas text-4xl sm:text-6xl font-bold ${match.score_home > match.score_away ? "text-pink-500" : "text-slate-900"}`}>{match.score_home ?? "-"}</span>
                <span className="text-slate-300 text-xl">·</span>
                <span className={`font-bebas text-4xl sm:text-6xl font-bold ${match.score_away > match.score_home ? "text-pink-500" : "text-slate-900"}`}>{match.score_away ?? "-"}</span>
              </div>
            ) : (
              <div className="font-blackhawk text-7xl sm:text-8xl italic tracking-widest" style={{ color: SECONDARY }}>VS</div>
            )}
            {isLive && <div className="mt-1 inline-block px-2 py-0.5 rounded text-[10px] font-bold bg-red-600 text-white animate-pulse">LIVE</div>}
          </div>

          {/* Away */}
          <div className="flex-1 flex flex-col items-center text-center">
            <ClubLink code={match.away_code} name={match.away_team} ourCode={ourCode}
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-slate-50 ring-2 ring-slate-100 flex items-center justify-center p-2 group-hover:scale-110 transition relative">
              <img src={match.away_logo} alt="" className="w-full h-full object-contain"/>
            </ClubLink>
            <ClubLink code={match.away_code} name={match.away_team} ourCode={ourCode}
              className="mt-3 font-black text-slate-900 text-sm sm:text-base uppercase">
              {match.away_team}
            </ClubLink>
          </div>
        </div>

        {/* Footer info */}
        <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
          <div className="flex items-center gap-1.5"><Calendar size={13} className="text-slate-400"/> {match.date} {match.time && <span className="font-bold text-slate-900">· {match.time}</span>}</div>
          {match.venue && <div className="flex items-center gap-1.5 truncate"><MapPin size={13} className="text-slate-400"/> {match.venue}</div>}
        </div>

        {kind === "next" && settings?.ticketing_url && (
          <a href={settings.ticketing_url} target="_blank" rel="noopener noreferrer"
            className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-bold text-xs uppercase tracking-wider shadow-md hover:scale-[1.02] transition"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
            <Ticket size={14}/> Billetterie
          </a>
        )}

        {/* Match résumé video CTA — only on past/finished matches */}
        {finished && (() => {
          const resume = findResumeVideo(match, videos || [], ourCode);
          const url = videoWatchUrl(resume);
          return (
            <a href={url || "/sport/medias"}
               target={url ? "_blank" : undefined}
               rel={url ? "noopener noreferrer" : undefined}
               className={`group/resume relative mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest shadow-md transition overflow-hidden ${url ? "text-white hover:scale-[1.02]" : "text-slate-500 bg-slate-100 cursor-not-allowed"}`}
               style={url ? { background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, boxShadow: `0 8px 24px ${SECONDARY}55` } : {}}
               onClick={(e) => { if (!url) e.preventDefault(); }}
               data-testid="match-resume-link"
               aria-disabled={!url}>
              {url && (
                <span className="absolute inset-0 -translate-x-full group-hover/resume:translate-x-full transition-transform duration-700 pointer-events-none"
                  style={{ background: "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)" }}/>
              )}
              {url ? <AnimatedPlay size={14}/> : <Play size={14}/>}
              <span className="relative">{url ? "Voir le résumé du match" : "Résumé indisponible"}</span>
            </a>
          );
        })()}
      </div>
    </motion.div>
  );
}

function MatchHighlightSection({ settings, next, last }) {
  return <PerformanceConsoleSection settings={settings} next={next} last={last}/>;
}

// ======================================================================
// PERFORMANCE DASHBOARD — 2-col combined layout (Next/Last left, Standings right)
// Replaces the previous separate MatchHighlight + Standings sections to avoid
// excessive vertical scrolling and provide a unified "console" feel.
// ======================================================================
function RankChip({ code, name, standings }) {
  if (!standings || !standings.length) return null;
  // Match by team_code first, then by team name (LNH API returns null team_code on next-match)
  const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");
  const target = norm(name);
  let idx = code ? standings.findIndex((r) => sameTeam(r.team_code, code)) : -1;
  if (idx < 0 && target) {
    idx = standings.findIndex((r) => {
      const a = norm(r.team_name || r.team || "");
      return a && (a === target || a.startsWith(target) || target.startsWith(a));
    });
  }
  if (idx < 0) return null;
  const row = standings[idx];
  const rank = row.rank ?? row.position ?? (idx + 1);
  const total = standings.length;
  const points = row.points ?? row.pts;
  return (
    <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[9px] uppercase tracking-wider"
      style={{ background: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.18)" }}
      data-testid={`rank-chip-${code || norm(name) || idx}`}>
      <span className="font-bebas font-black tabular-nums" style={{ fontSize: "0.95rem", color: SECONDARY, lineHeight: 1 }}>
        {rank}<sup className="text-[0.6em]">e</sup>
      </span>
      <span className="text-white/55">/</span>
      <span className="text-white/85 font-bold">{total}</span>
      {(points || points === 0) && (
        <>
          <span className="text-white/30">·</span>
          <span className="text-white/85 font-bold">{points} pts</span>
        </>
      )}
    </div>
  );
}

// Live score + half clock displayed where countdown used to be
function LiveScoreboard({ session, ourCode, kickoffIso }) {
  const [tickMs, setTickMs] = React.useState(Date.now());

  React.useEffect(() => {
    const id = setInterval(() => setTickMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!session) return null;
  const status = session.status;
  const period = session.period || 1;
  const isPlay = status === "playing";
  const isHalfTime = status === "halftime";
  const isFinished = status === "finished";

  // Compute live clock for current period
  const computeMs = (p) => {
    if (status === "finished") {
      return p === 1 ? (session.period_1_duration_ms || 0) : (session.period_2_duration_ms || 0);
    }
    if (status === "halftime" && p === 1) return session.period_1_duration_ms || 0;
    if (status === "halftime" && p === 2) return 0;
    if (period !== p) {
      // Inactive period
      return p === 1 ? (session.period_1_duration_ms || 0) : (session.period_2_duration_ms || 0);
    }
    // Active period — compute from chrono_started_at
    if (!session.chrono_started_at) return 0;
    const startMs = new Date(session.chrono_started_at).getTime();
    const totalPaused = (session.chrono_paused_ms || 0) +
      (session.chrono_paused_at ? Math.max(0, tickMs - new Date(session.chrono_paused_at).getTime()) : 0);
    return Math.max(0, tickMs - startMs - totalPaused);
  };

  const fmt = (ms) => {
    if (!ms || ms < 0) return "--:--";
    const total = Math.floor(ms / 1000);
    const m = String(Math.floor(total / 60)).padStart(2, "0");
    const s = String(total % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const homeIsUs = sameTeam(session.home_team, "istres-provence-handball") ||
                   /istres|iphb/i.test(session.home_team || "");
  const homeScore = session.home_score ?? 0;
  const awayScore = session.away_score ?? 0;

  const periodLabel = isHalfTime ? "Mi-temps"
    : isFinished ? "Terminé"
    : period === 2 ? "2ᵉ MI-TEMPS"
    : "1ʳᵉ MI-TEMPS";

  const mt1 = fmt(computeMs(1));
  const mt2 = fmt(computeMs(2));

  return (
    <div className="w-full">
      {/* Live status pill */}
      <div className="text-center mb-2">
        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full font-black uppercase text-[10px] tracking-[0.3em]"
          style={{ background: isFinished ? "rgba(255,255,255,0.1)" : `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "#fff" }}
          data-testid="home-live-status">
          {!isFinished && <span className="w-2 h-2 rounded-full bg-white animate-pulse"/>}
          {periodLabel}
        </span>
      </div>

      {/* Big score */}
      <div className="grid grid-cols-3 items-center gap-2 mb-3 text-center">
        <div className="font-bebas font-black tabular-nums leading-none text-white"
          style={{ fontSize: "clamp(2.6rem, 12vw, 4rem)" }}
          data-testid="home-live-home-score">
          {homeScore}
        </div>
        <div className="text-white/40 text-2xl font-light">·</div>
        <div className="font-bebas font-black tabular-nums leading-none text-white"
          style={{ fontSize: "clamp(2.6rem, 12vw, 4rem)" }}
          data-testid="home-live-away-score">
          {awayScore}
        </div>
      </div>

      {/* Two halves clocks */}
      <div className="flex items-stretch gap-2 justify-center" data-testid="home-live-halves">
        <div className={`flex-1 max-w-[8rem] px-3 py-2 rounded-xl text-center ${period === 1 && isPlay ? "ring-2" : "border border-white/15"}`}
          style={{
            background: period === 1 && (isPlay || isHalfTime) ? `linear-gradient(135deg, ${SECONDARY}33, ${PRIMARY}55)` : "rgba(255,255,255,0.05)",
            boxShadow: period === 1 && isPlay ? `0 0 20px ${SECONDARY}66` : "none",
            ...(period === 1 && isPlay ? { borderColor: SECONDARY } : {}),
          }}>
          <div className="text-[8px] uppercase tracking-[0.25em] font-bold mb-0.5"
            style={{ color: period === 1 ? "#fff" : "rgba(255,255,255,0.45)" }}>1ʳᵉ Mi-temps</div>
          <div className="font-bebas font-black tabular-nums leading-none"
            style={{ fontSize: "clamp(1.1rem, 4.6vw, 1.5rem)", color: period === 1 ? "#fff" : "rgba(255,255,255,0.55)" }}
            data-testid="home-live-mt1">
            {mt1}
          </div>
        </div>
        <div className={`flex-1 max-w-[8rem] px-3 py-2 rounded-xl text-center ${period === 2 && isPlay ? "ring-2" : "border border-white/15"}`}
          style={{
            background: period === 2 && isPlay ? `linear-gradient(135deg, ${SECONDARY}33, ${PRIMARY}55)` : "rgba(255,255,255,0.05)",
            boxShadow: period === 2 && isPlay ? `0 0 20px ${SECONDARY}66` : "none",
            ...(period === 2 && isPlay ? { borderColor: SECONDARY } : {}),
          }}>
          <div className="text-[8px] uppercase tracking-[0.25em] font-bold mb-0.5"
            style={{ color: period === 2 ? "#fff" : "rgba(255,255,255,0.45)" }}>2ᵉ Mi-temps</div>
          <div className="font-bebas font-black tabular-nums leading-none"
            style={{ fontSize: "clamp(1.1rem, 4.6vw, 1.5rem)", color: period === 2 ? "#fff" : "rgba(255,255,255,0.55)" }}
            data-testid="home-live-mt2">
            {mt2}
          </div>
        </div>
      </div>
    </div>
  );
}

function useLiveMatch(kickoffIso) {
  const [live, setLive] = React.useState(false);
  const [session, setSession] = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    let timer;
    const check = async () => {
      try {
        const r = await api.get("/match-live/active");
        if (!alive) return;
        const s = r.data?.session;
        const isPlay = s && (s.status === "playing" || s.status === "halftime");
        if (isPlay) { setSession(s); setLive(true); return; }
        if (s && s.status === "finished" && s.finished_at) {
          const fa = new Date(s.finished_at).getTime();
          if (Date.now() - fa < 30 * 60 * 1000) { setSession(s); setLive(true); return; }
        }
      } catch (_) {}
      if (kickoffIso) {
        const t = new Date(kickoffIso).getTime();
        const now = Date.now();
        if (!isNaN(t) && now > t && now - t < 2 * 60 * 60 * 1000) {
          if (alive) { setSession(null); setLive(true); }
          return;
        }
      }
      if (alive) { setSession(null); setLive(false); }
    };
    check();
    timer = setInterval(check, 5000);
    return () => { alive = false; clearInterval(timer); };
  }, [kickoffIso]);

  return { live, session };
}

function NextMatchHero({ next }) {
  const kickoffIso = kickoffDate(next);
  const { live, session } = useLiveMatch(kickoffIso);
  const { settings: globalSettings } = useSettings();
  const perfsWallEnabled = globalSettings?.sport_perfs_wall_enabled !== false;

  return (
    <div
      className="relative mt-6 flex flex-col items-center gap-3 rounded-2xl py-4 px-3 sm:px-6"
      style={{
        background: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.25) 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -1px 0 rgba(0,0,0,0.5)",
      }}
    >
      {!live && (
        <>
          <div className="font-black uppercase tracking-[0.35em] text-[10px] text-pink-300/80">
            Coup d'envoi dans
          </div>
          <MatchCountdown target={kickoffIso}/>
        </>
      )}
      {live && session && (
        <LiveScoreboard session={session} kickoffIso={kickoffIso}/>
      )}
      {live && !session && (
        <div className="text-center w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full font-black uppercase text-[10px] tracking-[0.3em]"
            style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "#fff" }}>
            <span className="w-2 h-2 rounded-full bg-white animate-pulse"/>
            Match en cours
          </div>
          <div className="mt-2 text-white/60 text-xs">Le live n'a pas encore été activé par l'opérateur.</div>
        </div>
      )}

      {/* CTA Suivre sur Perfs Wall — caché si module désactivé */}
      {live && perfsWallEnabled && (
        <Link
          to="/sport/perfs-wall"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="live-match-link"
          className="mt-1 inline-flex items-center gap-2 px-4 py-2.5 rounded-full font-black uppercase text-[11px] tracking-[0.18em] text-white shadow-[0_8px_22px_rgba(236,31,124,0.55)] hover:scale-[1.04] active:scale-[0.98] transition relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, #ff3a8c, ${SECONDARY})` }}
        >
          <span className="absolute inset-0 opacity-40 pointer-events-none"
            style={{ background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.45) 50%, transparent 70%)", animation: "iphbShine 2.4s ease-in-out infinite" }}/>
          <span className="relative inline-flex items-center gap-1.5">
            <Eye size={13}/> Suivre sur Perfs Wall
          </span>
          <ArrowRight size={13} className="relative"/>
          <style>{`@keyframes iphbShine { 0% { transform: translateX(-120%); } 60% { transform: translateX(120%); } 100% { transform: translateX(120%); } }`}</style>
        </Link>
      )}

      {/* MVP vote CTA — visible during live match and ~30 min after */}
      {live && <MvpVoteCta/>}
    </div>
  );
}

function MvpVoteCta() {
  return (
    <Link
      to="/sport/mon-espace?next=vote"
      data-testid="home-mvp-vote-cta"
      className="mt-4 group block w-full rounded-2xl px-4 py-3 text-center font-black uppercase text-[11px] tracking-[0.18em] text-white shadow-[0_10px_24px_rgba(236,31,124,0.4)] hover:scale-[1.02] active:scale-[0.99] transition relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(236,31,124,0.18) 100%)", border: `1px solid ${SECONDARY}88` }}
    >
      <span className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ background: "linear-gradient(115deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)", animation: "iphbShineMvp 3s ease-in-out infinite" }}/>
      <span className="relative inline-flex items-center justify-center gap-2 flex-wrap">
        <Award size={14} className="text-pink-300"/>
        <span>Votez pour l'homme du match</span>
        <span className="text-pink-200/70 normal-case tracking-normal text-[10px]">— connectez-vous</span>
        <ArrowRight size={12} className="text-pink-300 group-hover:translate-x-1 transition"/>
      </span>
      <style>{`@keyframes iphbShineMvp { 0% { transform: translateX(-120%); } 65% { transform: translateX(120%); } 100% { transform: translateX(120%); } }`}</style>
    </Link>
  );
}

function PerformanceDashboardSection({ settings, next, last, mvpWinner, standings = [] }) {
  const ourCode = ourTeamCode(settings);
  const ticketingUrl = settings?.ticketing_url || "https://billetterie.istreshandball.com/";
  const podiumColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
  const bgImage = settings?.match_console_bg
    || "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A9819-scaled-e1774454113383.jpg";
  // Live match state — drives title swap "Prochain match" → "Match en cours"
  const kickoff = next ? kickoffDate(next) : null;
  const { live: liveMatch } = useLiveMatch(kickoff);
  const [showProno, setShowProno] = React.useState(false);
  // Prono ouvert uniquement avant kickoff
  const pronoOpen = !!next && !!kickoff && !liveMatch && new Date(kickoff).getTime() > Date.now();

  // Auto-ouverture du modal de prono si on arrive avec ?openProno=1 (depuis Mon Espace)
  React.useEffect(() => {
    if (!pronoOpen) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("openProno") === "1") {
      setShowProno(true);
      // Nettoie l'URL pour ne pas re-déclencher au refresh
      const cleaned = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleaned);
    }
  }, [pronoOpen]);

  // Auto-scroll standings to Istres row
  const standingsRef = React.useRef(null);
  React.useEffect(() => {
    if (!standings.length || !standingsRef.current) return;
    const ourIdx = standings.findIndex((r) => sameTeam(r.team_code, ourCode));
    if (ourIdx >= 0) {
      const tr = standingsRef.current.querySelector(`tr[data-row-idx="${ourIdx}"]`);
      if (tr && tr.offsetTop !== undefined) {
        // Center the row in the scroll container (offset = row top - half container)
        const container = standingsRef.current;
        const target = tr.offsetTop - container.clientHeight / 2 + tr.clientHeight / 2;
        container.scrollTo({ top: Math.max(0, target), behavior: "smooth" });
      }
    }
  }, [standings, ourCode]);

  // Recent results — load past matches for left column to balance the standings column
  const [pastMatches, setPastMatches] = React.useState([]);
  React.useEffect(() => {
    api.get("/sport/calendar?limit=12").then((r) => {
      const all = r.data?.calendar || [];
      const today = new Date().toISOString().slice(0, 10);
      const finished = all.filter((m) => (m.status === "finished")
        || (((m.score_home || 0) + (m.score_away || 0)) > 0 && (m.date_iso || "") < today))
        .sort((a, b) => (b.date_iso || "").localeCompare(a.date_iso || ""))
        .slice(0, 4);
      setPastMatches(finished);
    }).catch(() => {});
  }, []);

  // YouTube videos — to find résumé video for each past match
  const [videos, setVideos] = React.useState([]);
  React.useEffect(() => {
    api.get("/sport/videos").then((r) => {
      setVideos(r.data?.videos || r.data?.items || []);
    }).catch(() => {});
  }, []);

  return (
    <section data-testid="performance-dashboard" className="relative overflow-hidden" style={{ background: BG_DARK }}>
      {/* BG photo + violet overlay */}
      <div className="absolute inset-0 z-0">
        <img src={bgImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 grayscale"/>
        <div className="absolute inset-0" style={{
          background: `linear-gradient(180deg, rgba(22,7,50,0.96) 0%, rgba(79,27,124,0.88) 50%, rgba(22,7,50,0.96) 100%)`,
        }}/>
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1px)`,
          backgroundSize: "16px 16px",
          maskImage: "radial-gradient(ellipse at center, black, transparent 70%)",
        }}/>
      </div>

      <div className="relative z-[2] max-w-7xl mx-auto px-4 lg:px-8 py-20">
        {/* Section header */}
        <div className="text-center mb-10">
          <div className="inline-block text-pink-400 font-black tracking-[0.3em] text-xs uppercase mb-2">
            Saison 2025-2026 · Tableau de bord
          </div>
          <h2 className="font-black text-3xl sm:text-4xl uppercase italic text-white">
            Le club <span style={{ color: SECONDARY }}>en direct</span>
          </h2>
        </div>

        {/* 2-col grid: matches (left) + standings (right) */}
        <div className="grid lg:grid-cols-[1.05fr,0.95fr] gap-5 lg:gap-7 items-start">
          {/* LEFT COLUMN — Next match (top) + Last result (bottom) stacked */}
          <div className="space-y-5 min-w-0">
            {/* Top sub-card: Next match */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="relative rounded-3xl overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.5)] p-6 sm:p-7"
              style={{ background: `linear-gradient(140deg, ${PRIMARY} 0%, #2c0e4a 60%, #160732 100%)` }}
              data-testid="dashboard-next-match"
            >
              <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
                backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
                backgroundSize: "10px 10px",
                maskImage: "radial-gradient(ellipse at top right, black, transparent 60%)",
              }}/>

              <div className="relative flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <div className="text-pink-400 font-black tracking-[0.3em] text-[10px] uppercase">
                    {next?.is_offseason_fallback ? "Inter-saison" : (next?.league || "Liqui Moly StarLigue")}
                    {!next?.is_offseason_fallback && (next?.round || next?.matchday || next?.j) && <span className="ml-2 text-white/60">· J{next?.round || next?.matchday || next?.j}</span>}
                  </div>
                  <h3 className="text-white font-black uppercase italic text-xl sm:text-2xl mt-1 leading-tight flex items-center gap-2 flex-wrap" data-testid="dashboard-next-match-title">
                    {next?.is_offseason_fallback ? "Saison terminée" : (liveMatch ? "Match en cours" : "Prochain match")}
                    {liveMatch && (
                      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] tracking-[0.2em] font-black"
                        style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "#fff" }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"/>
                        LIVE
                      </span>
                    )}
                  </h3>
                </div>
                {!next?.is_offseason_fallback && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {pronoOpen && (
                      <button
                        type="button"
                        onClick={() => setShowProno(true)}
                        data-testid="dashboard-prono-match"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-black uppercase text-[11px] tracking-widest shadow-lg hover:scale-105 transition border-2"
                        style={{ background: "rgba(236,31,124,0.15)", borderColor: SECONDARY, color: "#fff" }}
                      >
                        <Target size={13} className="text-pink-300"/> Prono Match
                      </button>
                    )}
                    <a href={ticketingUrl} target="_blank" rel="noopener noreferrer"
                      data-testid="dashboard-billetterie"
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-black uppercase text-[11px] tracking-widest shadow-lg hover:scale-105 transition"
                      style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
                      <Ticket size={13}/> Billetterie
                    </a>
                  </div>
                )}
              </div>

              {next?.is_offseason_fallback ? (
                <div className="relative mt-6 flex flex-col items-center text-center gap-4 py-4">
                  <div className="text-5xl sm:text-6xl">🏆</div>
                  <div className="text-white/80 max-w-md text-sm leading-relaxed">
                    La saison Liqui Moly StarLigue 2025-26 est terminée.
                    Nos taureaux reviennent fouler le parquet en{" "}
                    <span className="text-pink-300 font-black uppercase tracking-widest">septembre 2026</span>.
                  </div>
                  <Link to="/sport/calendrier"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-black uppercase text-[11px] tracking-widest text-white border border-white/20 bg-white/5 hover:bg-white/10 hover:border-pink-400/60 transition"
                    data-testid="offseason-cta-calendar">
                    <Calendar size={13}/> Revivre la saison <ArrowRight size={12}/>
                  </Link>
                </div>
              ) : next ? (
                <>
                  <div className="relative mt-5 grid grid-cols-[1fr,auto,1fr] items-center gap-3 sm:gap-5">
                    <ClubLink code={next.home_code} name={next.home_team} ourCode={ourCode}
                      className="flex flex-col items-center group">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white p-2.5 ring-2 ring-white/10 shadow-2xl group-hover:scale-105 transition">
                        <img src={next.home_logo} alt={next.home_team} className="w-full h-full object-contain"/>
                      </div>
                      <div className="text-white text-xs font-bold uppercase mt-2 text-center max-w-[110px] truncate">{next.home_team}</div>
                      <RankChip code={next.home_code} standings={standings}/>
                    </ClubLink>

                    <div className="flex flex-col items-center text-center">
                      <span className="font-blackhawk italic text-7xl sm:text-8xl tracking-widest" style={{ color: SECONDARY }}>VS</span>
                      <div className="text-pink-400 font-black text-base sm:text-lg mt-1">
                        {next?.date_iso ? fmtFr(next.date_iso) : (next?.date || "—")}
                      </div>
                      <div className="text-white/85 text-[11px] font-bold uppercase tracking-widest">{next?.time || "—"}</div>
                      {next?.venue && (
                        <div className="text-white/60 text-[10px] mt-1 italic max-w-[140px] truncate">{next.venue}</div>
                      )}
                    </div>

                    <ClubLink code={next.away_code} name={next.away_team} ourCode={ourCode}
                      className="flex flex-col items-center group relative">
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white p-2.5 ring-2 ring-white/10 shadow-2xl group-hover:scale-105 transition">
                        <img src={next.away_logo} alt={next.away_team} className="w-full h-full object-contain"/>
                      </div>
                      <div className="text-white text-xs font-bold uppercase mt-2 text-center max-w-[110px] truncate">{next.away_team}</div>
                      <RankChip code={next.away_code} standings={standings}/>
                    </ClubLink>
                  </div>

                  {/* Scoreboard countdown to kick-off OR live chrono */}
                  <NextMatchHero
                    next={next}
                    standings={standings}
                  />
                </>
              ) : (
                <div className="mt-6 py-8 text-center text-white/50 italic">Pas de prochain match programmé</div>
              )}
            </motion.div>

            {/* Modal pronostic — affichée à la demande */}
            {showProno && next && (
              <MatchPronoModal
                match={next}
                kickoffIso={kickoff ? new Date(kickoff).toISOString() : null}
                onClose={() => setShowProno(false)}
              />
            )}

            {/* Bottom sub-card: Last result */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="relative rounded-3xl overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.5)] p-6 sm:p-7"
              style={{ background: `linear-gradient(140deg, #2c0e4a 0%, ${PRIMARY} 60%, ${BG_DEEP} 100%)` }}
              data-testid="dashboard-last-result"
            >
              {last && (() => {
                const homeIsUs = sameTeam(last.home_code, ourCode);
                const ourScore = homeIsUs ? last.score_home : last.score_away;
                const oppScore = homeIsUs ? last.score_away : last.score_home;
                const oppCode = homeIsUs ? last.away_code : last.home_code;
                const oppName = homeIsUs ? last.away_team : last.home_team;
                const oppLogo = homeIsUs ? last.away_logo : last.home_logo;
                const isWin = ourScore > oppScore;
                return (
                  <>
                    {isWin && <ConfettiBurst count={26}/>}
                    <div className="relative flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${homeIsUs ? "bg-pink-500/25 text-pink-200 border border-pink-400/40" : "bg-sky-500/25 text-sky-200 border border-sky-400/40"}`}
                              data-testid="last-result-pastille">
                          {homeIsUs ? <><Home size={11}/> Domicile</> : <><Plane size={11}/> Extérieur</>}
                        </span>
                        <div>
                          <div className="text-pink-400 font-black tracking-[0.3em] text-[10px] uppercase">Dernier résultat</div>
                          <h3 className="text-white font-black uppercase italic text-lg sm:text-xl mt-1 leading-tight">
                            {isWin ? "Victoire IPHB" : "Match disputé"}
                          </h3>
                        </div>
                      </div>
                      <Link to="/sport/calendrier" data-testid="dashboard-calendar-cta"
                        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full font-black uppercase text-[10px] tracking-widest border-2 border-pink-400 text-pink-300 hover:bg-pink-500 hover:text-white transition">
                        Calendrier complet <ArrowRight size={11}/>
                      </Link>
                    </div>

                    <div className="relative mt-5 grid grid-cols-[1fr,auto,1fr] items-center gap-3">
                      <ClubLink code={oppCode} name={oppName} ourCode={ourCode}
                        className="flex flex-col items-center group">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white p-2 ring-2 ring-white/10 shadow-xl group-hover:scale-105 transition">
                          <img src={oppLogo} alt={oppName} className="w-full h-full object-contain"/>
                        </div>
                        <div className="text-white/80 text-[10px] font-bold uppercase mt-1.5 text-center max-w-[100px] truncate">{oppName}</div>
                      </ClubLink>

                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className={`font-bebas text-4xl sm:text-5xl font-bold ${last.score_home > last.score_away ? "text-pink-400" : "text-white"}`}>
                          {last.score_home ?? "-"}
                        </span>
                        <span className="text-white/30 text-xl font-thin">|</span>
                        <span className={`font-bebas text-4xl sm:text-5xl font-bold ${last.score_away > last.score_home ? "text-pink-400" : "text-white"}`}>
                          {last.score_away ?? "-"}
                        </span>
                      </div>

                      <div className="flex flex-col items-center">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white p-2 ring-2 ring-white/10 shadow-xl">
                          <img src={settings?.logo_url || "https://lnh.fr/medias/sports_teams/istres__logo__2023-2024.png"}
                            alt="IPH" className="w-full h-full object-contain"/>
                        </div>
                        <div className="text-pink-400 text-[10px] font-bold uppercase mt-1.5">IPHB</div>
                      </div>
                    </div>
                    {/* Résumé video CTA */}
                    {(() => {
                      const resume = findResumeVideo(last, videos, ourCode);
                      const url = videoWatchUrl(resume);
                      if (!url) return null;
                      return (
                        <a href={url} target="_blank" rel="noopener noreferrer"
                          className="group/resume relative mt-5 inline-flex items-center justify-center gap-2 w-full px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-[1.02] transition overflow-hidden"
                          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, boxShadow: `0 10px 28px ${SECONDARY}66` }}
                          data-testid="last-result-resume-link">
                          <span className="absolute inset-0 -translate-x-full group-hover/resume:translate-x-full transition-transform duration-700 pointer-events-none"
                            style={{ background: "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.35) 50%, transparent 100%)" }}/>
                          <AnimatedPlay size={13}/>
                          <span className="relative">Voir le résumé du match</span>
                          <ArrowRight size={13} className="relative"/>
                        </a>
                      );
                    })()}
                  </>
                );
              })()}
              {!last && <div className="py-8 text-center text-white/50 italic">Aucun résultat récent</div>}
              {/* MVP Winner — sondage public du dernier match terminé */}
              {mvpWinner?.player_name && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25, duration: 0.5 }}
                  className="relative mt-5 rounded-2xl overflow-hidden"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}33, ${PRIMARY}55)`, border: `1px solid ${SECONDARY}66` }}
                  data-testid="last-result-mvp"
                >
                  <div className="px-4 py-3 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                      <Trophy size={18} className="text-white"/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-pink-300 font-black tracking-[0.25em] text-[9px] uppercase">
                        MVP du match · Vote des supporters
                      </div>
                      <div className="font-blackhawk italic uppercase text-white text-lg leading-tight truncate" data-testid="last-result-mvp-name">
                        {mvpWinner.player_name}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-bebas font-black text-pink-300 tabular-nums text-2xl leading-none" data-testid="last-result-mvp-percent">
                        {mvpWinner.percent}%
                      </div>
                      <div className="text-white/55 text-[10px] uppercase tracking-wider">
                        {mvpWinner.votes}/{mvpWinner.total_votes} votes
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Past results mini-list — fills the left column to match standings height */}
            {pastMatches.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.18 }}
                className="rounded-3xl bg-white/5 border border-white/10 overflow-hidden"
                data-testid="dashboard-past-matches"
              >
                <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                  <div>
                    <div className="text-pink-400 font-black tracking-[0.3em] text-[10px] uppercase">Matchs précédents</div>
                    <div className="text-white/60 text-[11px] mt-0.5">Les derniers résultats du club</div>
                  </div>
                  <Link to="/sport/calendrier" className="inline-flex items-center gap-1 text-pink-400 hover:text-pink-300 text-[10px] font-black uppercase tracking-widest">
                    Tout voir <ArrowRight size={11}/>
                  </Link>
                </div>
                <div className="divide-y divide-white/5">
                  {pastMatches.map((m, i) => {
                    const homeIsUs = sameTeam(m.home_code, ourCode);
                    const ourScore = homeIsUs ? m.score_home : m.score_away;
                    const oppScore = homeIsUs ? m.score_away : m.score_home;
                    const win = ourScore > oppScore;
                    const draw = ourScore === oppScore;
                    const resume = findResumeVideo(m, videos, ourCode);
                    const resumeUrl = videoWatchUrl(resume);
                    return (
                      <div key={m.id || i} className="px-5 py-2.5 flex items-center gap-3 grayscale-[0.6] hover:grayscale-0 hover:bg-white/5 transition opacity-80 hover:opacity-100">
                        <span className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black uppercase ${
                          win ? "bg-emerald-500/30 text-emerald-300" : draw ? "bg-amber-500/30 text-amber-300" : "bg-rose-500/30 text-rose-300"
                        }`}>
                          {win ? "V" : draw ? "N" : "D"}
                        </span>
                        {/* Home/Away pastille */}
                        <span
                          className={`shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full ${homeIsUs ? "bg-pink-500/30 text-pink-200" : "bg-sky-500/30 text-sky-200"}`}
                          title={homeIsUs ? "Match à domicile" : "Match à l'extérieur"}
                        >
                          {homeIsUs ? <Home size={11}/> : <Plane size={11}/>}
                        </span>
                        <span className="text-white/40 text-[10px] font-bold tracking-widest uppercase shrink-0 w-14">
                          {m.date_iso ? new Date(m.date_iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }) : "—"}
                        </span>
                        <div className="flex-1 flex items-center gap-2 min-w-0">
                          <img src={m.home_logo} alt="" className="w-5 h-5 object-contain shrink-0"/>
                          <span className={`text-[11px] font-bold truncate ${homeIsUs ? "text-pink-300" : "text-white/80"}`}>{m.home_team}</span>
                          <span className={`font-black text-xs ml-auto ${m.score_home > m.score_away ? "text-pink-400" : "text-white/80"}`}>{m.score_home}</span>
                          <span className="text-white/30 text-[10px]">-</span>
                          <span className={`font-black text-xs ${m.score_away > m.score_home ? "text-pink-400" : "text-white/80"}`}>{m.score_away}</span>
                          <span className={`text-[11px] font-bold truncate text-right ${!homeIsUs ? "text-pink-300" : "text-white/80"}`}>{m.away_team}</span>
                          <img src={m.away_logo} alt="" className="w-5 h-5 object-contain shrink-0"/>
                        </div>
                        {/* Résumé video link */}
                        {resumeUrl ? (
                          <a href={resumeUrl} target="_blank" rel="noopener noreferrer"
                            className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full bg-pink-500/20 hover:bg-pink-500 text-pink-300 hover:text-white transition shadow-[0_0_0_0_rgba(236,31,124,0)] hover:shadow-[0_0_18px_rgba(236,31,124,0.6)]"
                            title="Voir le résumé du match"
                            data-testid={`past-resume-${m.id || i}`}
                            onClick={(e) => e.stopPropagation()}>
                            <AnimatedPlay size={12} subtle/>
                          </a>
                        ) : (
                          <span className="shrink-0 w-7 h-7 inline-flex items-center justify-center rounded-full bg-white/5 text-white/20" title="Résumé indisponible"><Play size={12}/></span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </div>

          {/* RIGHT COLUMN — Standings */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="rounded-3xl bg-white shadow-[0_25px_60px_rgba(0,0,0,0.5)] overflow-hidden min-w-0"
            data-testid="dashboard-standings"
          >
            <div className="px-5 py-4 flex items-center justify-between" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
              <div>
                <div className="text-pink-300 font-black tracking-[0.3em] text-[10px] uppercase">StarLigue 25-26</div>
                <h3 className="text-white font-black uppercase italic text-lg sm:text-xl leading-tight mt-0.5">Classement</h3>
              </div>
              <Trophy size={22} className="text-pink-400"/>
            </div>

            {standings.length === 0 ? (
              <div className="py-12 text-center text-slate-400 italic text-sm">Classement non disponible.</div>
            ) : (
              <div ref={standingsRef} className="overflow-x-auto max-h-[640px] overflow-y-auto">
                <table className="w-full text-[13px]">
                  <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr className="text-slate-600 text-[10px] uppercase tracking-widest font-black border-b border-slate-200">
                      <th className="px-3 py-3 text-left">#</th>
                      <th className="px-3 py-3 text-left">Équipe</th>
                      <th className="px-2 py-3 text-center">Pts</th>
                      <th className="px-2 py-3 text-center hidden sm:table-cell">J</th>
                      <th className="px-2 py-3 text-center hidden md:table-cell">G</th>
                      <th className="px-2 py-3 text-center hidden md:table-cell">N</th>
                      <th className="px-2 py-3 text-center hidden md:table-cell">P</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standings.map((r, i) => {
                      const isOur = sameTeam(r.team_code, ourCode) || sameTeam(r.team_name, "istres");
                      return (
                        <tr key={r.team_code || r.rank}
                          data-row-idx={i}
                          className={`border-b border-slate-100 transition ${
                            isOur ? "text-white font-bold" : i % 2 === 0 ? "bg-white hover:bg-pink-50/60" : "bg-slate-50/50 hover:bg-pink-50/60"
                          }`}
                          style={isOur ? { background: `linear-gradient(90deg, ${PRIMARY}, #4a1f3f)` } : {}}>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center justify-center w-7 h-7 rounded-md font-black text-xs"
                              style={{
                                background: r.rank <= 3 ? podiumColors[r.rank - 1] : isOur ? "rgba(255,255,255,0.15)" : "#f1eaf3",
                                color: r.rank <= 3 ? "#1a0820" : isOur ? "white" : PRIMARY,
                              }}>
                              {r.rank}
                            </div>
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex items-center gap-2">
                              <ClubLink code={r.team_code} name={r.team_name} ourCode={ourCode}
                                className={`w-7 h-7 rounded-full flex items-center justify-center p-0.5 ${isOur ? "bg-white" : "bg-slate-50 ring-1 ring-slate-200"}`}>
                                {r.logo_url ? <img src={r.logo_url} alt="" className="w-full h-full object-contain"/> : null}
                              </ClubLink>
                              <ClubLink code={r.team_code} name={r.team_name} ourCode={ourCode}
                                className={`text-xs font-bold truncate max-w-[110px] sm:max-w-none ${isOur ? "text-white" : "text-slate-900 hover:text-pink-600"}`}>
                                {r.team_name}
                              </ClubLink>
                              {isOur && <span className="ml-1 px-1 py-0.5 text-[8px] font-black rounded uppercase bg-pink-500 text-white">Nous</span>}
                            </div>
                          </td>
                          <td className={`px-2 py-2.5 text-center font-black ${isOur ? "text-pink-300" : ""}`} style={isOur ? {} : { color: PRIMARY }}>{r.points}</td>
                          <td className={`px-2 py-2.5 text-center hidden sm:table-cell ${isOur ? "text-white/90" : "text-slate-700"}`}>{r.played}</td>
                          <td className="px-2 py-2.5 text-center hidden md:table-cell text-emerald-600 font-bold">{r.wins}</td>
                          <td className={`px-2 py-2.5 text-center hidden md:table-cell ${isOur ? "text-white/90" : "text-slate-500"}`}>{r.draws}</td>
                          <td className="px-2 py-2.5 text-center hidden md:table-cell text-rose-500 font-bold">{r.losses}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Link to="/sport/calendrier#standings" className="block px-5 py-3 text-center text-pink-600 font-black uppercase tracking-widest text-[11px] hover:bg-pink-50 transition border-t border-slate-100">
              Voir le classement complet →
            </Link>
          </motion.div>
        </div>
      </div>

      <TornEdge position="bottom" color="#ffffff" variant="shred" height={64}/>
    </section>
  );
}

// ======================================================================
// PERFORMANCE CONSOLE — reference-style 3-card layout with torn edges
// ======================================================================
function PerformanceConsoleSection({ settings, next, last }) {
  const ourCode = ourTeamCode(settings);

  // Background image: photo of the match action, blurred + violet overlay
  const bgImage = settings?.match_console_bg
    || "/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F03%2F0J5A9819-scaled-e1774454113383.jpg";

  return (
    <section className="relative overflow-hidden" style={{ background: BG_DARK }}>
      {/* Torn edge top (entering this section from previous) */}
      <TornEdge position="top" color="#ffffff" variant="shred" height={64}/>

      {/* BG photo — desaturated + violet overlay */}
      <div className="absolute inset-0 z-0">
        <img src={bgImage} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale"/>
        <div className="absolute inset-0" style={{
          background: `linear-gradient(180deg, rgba(22,7,50,0.95) 0%, rgba(79,27,124,0.85) 50%, rgba(22,7,50,0.95) 100%)`,
        }}/>
        {/* halftone dots */}
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1px)`,
          backgroundSize: "16px 16px",
          maskImage: "radial-gradient(ellipse at center, black, transparent 70%)",
        }}/>
      </div>

      <div className="relative z-[2] max-w-7xl mx-auto px-4 lg:px-8 py-24 sm:py-32">
        <ConsoleLayout settings={settings} next={next} last={last} ourCode={ourCode}/>
      </div>

      {/* Torn edge bottom */}
      <TornEdge position="bottom" color="#ffffff" variant="shred" height={64}/>
    </section>
  );
}

function fmtFr(date) {
  if (!date) return "";
  try { return new Date(date).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return date; }
}

function ConsoleLayout({ settings, next, last, ourCode }) {
  const ticketingUrl = settings?.ticketing_url || "https://billetterie.istreshandball.com/";

  return (
    <div className="grid lg:grid-cols-[1.4fr,0.9fr,1fr] gap-3 sm:gap-4 items-stretch relative">
      {/* CARD 1 — PROCHAIN MATCH (versus card) */}
      {next ? (
        <NextMatchCard match={next} ourCode={ourCode}/>
      ) : (
        <div className="rounded-2xl bg-[#280F4F] p-10 text-center text-white/50 italic min-h-[400px] flex items-center justify-center shadow-2xl">
          Pas de prochain match programmé
        </div>
      )}

      {/* CARD 2 — INFOS MATCH (date / heure / billetterie / calendrier) — taller, slightly elevated */}
      <CenterInfoCard match={next} ticketingUrl={ticketingUrl}/>

      {/* CARD 3 — RÉSULTAT DERNIER MATCH */}
      {last ? (
        <LastResultCard match={last} ourCode={ourCode}/>
      ) : (
        <div className="rounded-2xl bg-[#280F4F] p-10 text-center text-white/50 italic min-h-[400px] flex items-center justify-center shadow-2xl">
          Aucun résultat récent
        </div>
      )}
    </div>
  );
}

function NextMatchCard({ match, ourCode }) {
  const homeIsUs = sameTeam(match.home_code, ourCode);
  const isAway = !homeIsUs && match.away_code === ourCode;

  return (
    <motion.div
      initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.7 }}
      className="relative rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] overflow-hidden p-8 min-h-[420px] flex flex-col justify-between"
      style={{ background: `linear-gradient(150deg, ${PRIMARY} 0%, #2c0e4a 60%, #160732 100%)` }}
      data-testid="console-next-match"
    >
      {/* halftone deco */}
      <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: "10px 10px",
        maskImage: "radial-gradient(ellipse at top right, black, transparent 60%)",
      }}/>

      {/* Tag */}
      <h3 className="font-black uppercase italic text-3xl sm:text-4xl leading-[0.95] -tracking-tight"
        style={{ color: SECONDARY, textShadow: "0 0 24px rgba(236,31,124,0.6)" }}>
        Prochain<br/>match
      </h3>

      {/* Versus block */}
      <div className="relative flex items-center justify-around gap-4 my-6">
        <ClubLink code={match.home_code} name={match.home_team} ourCode={ourCode}
          className="flex flex-col items-center group">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white p-3 ring-2 ring-white/10 shadow-2xl group-hover:scale-110 transition relative">
            <img src={match.home_logo} alt={match.home_team} className="w-full h-full object-contain"/>
            {isAway && match.away_code === ourCode ? null : null}
          </div>
        </ClubLink>
        <span className="font-blackhawk italic text-7xl sm:text-8xl tracking-widest" style={{ color: SECONDARY }}>VS</span>
        <ClubLink code={match.away_code} name={match.away_team} ourCode={ourCode}
          className="flex flex-col items-center group relative">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white p-3 ring-2 ring-white/10 shadow-2xl group-hover:scale-110 transition">
            <img src={match.away_logo} alt={match.away_team} className="w-full h-full object-contain"/>
          </div>
          {isAway && (
            <div className="absolute -top-2 -right-2 w-9 h-9 rounded-full flex items-center justify-center text-white shadow-xl ring-4 ring-white"
              style={{ background: "linear-gradient(135deg, #38bdf8, #2563eb)" }}>
              <Plane size={15} className="rotate-[-30deg]"/>
            </div>
          )}
        </ClubLink>
      </div>
    </motion.div>
  );
}

function CenterInfoCard({ match, ticketingUrl }) {
  const dateStr = match?.date_iso ? fmtFr(match.date_iso) : (match?.date || "—");
  const timeStr = match?.time || "—";
  const venue = match?.venue || (match?.home_team || "");
  const j = match?.round || match?.matchday || match?.j;

  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.15 }}
      className="relative rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.6)] overflow-hidden p-6 sm:p-8 lg:-mt-8 lg:-mb-8 z-10 flex flex-col"
      style={{ background: `linear-gradient(180deg, ${BG_DEEP} 0%, ${PRIMARY} 60%, #2a0e4a 100%)` }}
      data-testid="console-info-card"
    >
      {/* Top: League + J */}
      <div className="text-center text-white/95 mb-6">
        <div className="font-bold text-base sm:text-lg italic">{match?.league || "Liqui Moly StarLigue"}</div>
        {j && <div className="text-pink-400 font-black text-xl sm:text-2xl mt-1">J{j}</div>}
      </div>

      {/* CTA Billetterie */}
      <a href={ticketingUrl} target="_blank" rel="noopener noreferrer"
        data-testid="console-billetterie"
        className="block w-full rounded-xl px-5 py-4 text-center font-black uppercase tracking-widest text-sm shadow-[0_10px_30px_rgba(236,31,124,0.5)] hover:scale-[1.02] transition mb-3"
        style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: "white" }}>
        🎟 Billetterie
      </a>

      {/* Stats grid */}
      <div className="space-y-2 my-3 text-center">
        <div className="rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 px-3 py-3">
          <div className="text-pink-400 font-black text-xl sm:text-2xl tracking-wider">{dateStr}</div>
        </div>
        <div className="rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 px-3 py-3">
          <div className="text-pink-400 font-black text-xl sm:text-2xl tracking-wider">{timeStr}</div>
        </div>
      </div>

      {/* Calendrier complet */}
      <Link to="/sport/calendrier"
        data-testid="console-calendar-cta"
        className="block w-full rounded-xl px-5 py-3 text-center font-black uppercase tracking-widest text-[11px] border-2 border-pink-400 text-pink-400 hover:bg-pink-500 hover:text-white transition mt-1">
        Calendrier complet
      </Link>

      <div className="mt-3 text-center text-white/85 text-sm font-medium">{venue}</div>
    </motion.div>
  );
}

function LastResultCard({ match, ourCode }) {
  const homeIsUs = sameTeam(match.home_code, ourCode);
  const ourScore = homeIsUs ? match.score_home : match.score_away;
  const oppScore = homeIsUs ? match.score_away : match.score_home;
  const oppCode = homeIsUs ? match.away_code : match.home_code;
  const oppName = homeIsUs ? match.away_team : match.home_team;
  const oppLogo = homeIsUs ? match.away_logo : match.home_logo;
  const isWin = ourScore > oppScore;

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.3 }}
      className="relative rounded-3xl shadow-[0_25px_60px_rgba(0,0,0,0.5)] overflow-hidden p-8 min-h-[420px] flex flex-col justify-between"
      style={{ background: `linear-gradient(150deg, ${PRIMARY} 0%, #2c0e4a 60%, ${BG_DEEP} 100%)` }}
      data-testid="console-last-result"
    >
      {isWin && <ConfettiBurst count={42}/>}

      {/* halftone deco */}
      <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
        backgroundSize: "10px 10px",
        maskImage: "radial-gradient(ellipse at top left, black, transparent 60%)",
      }}/>

      <h3 className="font-black uppercase italic text-3xl sm:text-4xl leading-[0.95] text-right -tracking-tight"
        style={{ color: SECONDARY, textShadow: "0 0 24px rgba(236,31,124,0.6)" }}>
        Résultat du<br/>dernier match
      </h3>

      {/* Score block */}
      <div className="relative flex items-center justify-around gap-4 my-6">
        <ClubLink code={oppCode} name={oppName} ourCode={ourCode}
          className="flex flex-col items-center group">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white p-3 ring-2 ring-white/10 shadow-2xl group-hover:scale-110 transition">
            <img src={oppLogo} alt={oppName} className="w-full h-full object-contain"/>
          </div>
        </ClubLink>
        <div className="text-white text-center">
          <div className="flex items-center gap-2">
            <span className={`font-bebas font-bold text-5xl sm:text-6xl ${isWin ? "text-pink-400" : "text-white"}`} data-testid="last-result-our-score">{ourScore ?? "-"}</span>
            <span className="text-white/30 text-2xl font-thin">|</span>
            <span className="font-bebas font-bold text-5xl sm:text-6xl text-white/80" data-testid="last-result-opp-score">{oppScore ?? "-"}</span>
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white p-3 ring-2 ring-white/10 shadow-2xl">
            <img src={homeIsUs ? match.home_logo : match.away_logo} alt="IPH" className="w-full h-full object-contain"
              onError={(e) => { e.currentTarget.src = "https://lnh.fr/medias/sports_teams/istres__logo__2023-2024.png"; }}/>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ======================================================================
// STANDINGS TABLE
// ======================================================================
export function StandingsSection({ rows = [], settings }) {
  const ourCode = ourTeamCode(settings);
  const podiumColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
  return (
    <section id="standings" className="relative py-20" style={{ background: "#f5f1f7" }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-10">
          <div className="inline-block text-pink-600 font-black tracking-[0.3em] text-xs uppercase mb-2">Saison 2025-2026</div>
          <h2 className="font-black text-3xl sm:text-4xl uppercase italic" style={{ color: PRIMARY }}>
            Classement <span style={{ color: SECONDARY }}>StarLigue</span>
          </h2>
        </motion.div>

        {rows.length === 0 ? (
          <div className="text-center text-slate-500 italic py-16 bg-white rounded-2xl shadow">
            Classement non disponible pour le moment.
          </div>
        ) : (
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="overflow-hidden rounded-2xl shadow-2xl bg-white border border-slate-100">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white text-[11px] uppercase tracking-widest font-black" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
                    <th className="px-3 py-4 text-left">#</th>
                    <th className="px-3 py-4 text-left">Équipe</th>
                    <th className="px-3 py-4 text-center">Pts</th>
                    <th className="px-3 py-4 text-center hidden sm:table-cell">J</th>
                    <th className="px-3 py-4 text-center hidden sm:table-cell">G</th>
                    <th className="px-3 py-4 text-center hidden sm:table-cell">N</th>
                    <th className="px-3 py-4 text-center hidden sm:table-cell">P</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const isOur = sameTeam(r.team_code, ourCode) || sameTeam(r.team_name, "istres");
                    return (
                      <motion.tr
                        key={r.team_code || r.rank}
                        initial={{ opacity: 0, x: -12 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: Math.min(i * 0.025, 0.5), duration: 0.35 }}
                        className={`border-b border-slate-100 group transition ${
                          isOur
                            ? "text-white font-bold"
                            : i % 2 === 0 ? "bg-white hover:bg-pink-50" : "bg-slate-50/60 hover:bg-pink-50"
                        }`}
                        style={isOur ? { background: `linear-gradient(90deg, ${PRIMARY}, #4a1f3f)` } : {}}
                      >
                        <td className="px-3 py-3.5">
                          <div className="flex items-center justify-center w-9 h-9 rounded-lg font-black"
                            style={{
                              background: r.rank <= 3 ? podiumColors[r.rank - 1] : isOur ? "rgba(255,255,255,0.15)" : "#f1eaf3",
                              color: r.rank <= 3 ? "#1a0820" : isOur ? "white" : PRIMARY,
                            }}>
                            {r.rank}
                          </div>
                        </td>
                        <td className="px-3 py-3.5">
                          <div className="flex items-center gap-3">
                            <ClubLink code={r.team_code} name={r.team_name} ourCode={ourCode}
                              className={`w-9 h-9 rounded-full flex items-center justify-center p-1 ${isOur ? "bg-white" : "bg-slate-50 ring-1 ring-slate-200"}`}>
                              {r.logo_url ? <img src={r.logo_url} alt="" className="w-full h-full object-contain"/> : null}
                            </ClubLink>
                            <ClubLink code={r.team_code} name={r.team_name} ourCode={ourCode}
                              className={`font-bold ${isOur ? "text-white" : "text-slate-900 hover:text-pink-600"}`}>
                              {r.team_name}
                            </ClubLink>
                            {isOur && <span className="ml-1 px-1.5 py-0.5 text-[9px] font-black rounded uppercase bg-pink-500 text-[#1a0820]">Nous</span>}
                          </div>
                        </td>
                        <td className={`px-3 py-3.5 text-center font-black text-lg ${isOur ? "text-pink-300" : ""}`} style={isOur ? {} : { color: PRIMARY }}>{r.points}</td>
                        <td className={`px-3 py-3.5 text-center hidden sm:table-cell ${isOur ? "text-white/90" : "text-slate-700"}`}>{r.played}</td>
                        <td className="px-3 py-3.5 text-center hidden sm:table-cell text-emerald-600 font-bold">{r.wins}</td>
                        <td className={`px-3 py-3.5 text-center hidden sm:table-cell ${isOur ? "text-white/90" : "text-slate-500"}`}>{r.draws}</td>
                        <td className="px-3 py-3.5 text-center hidden sm:table-cell text-rose-500 font-bold">{r.losses}</td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
      <TornEdge position="bottom" color={BG_DARK} variant="jagged" height={48}/>
    </section>
  );
}

// ======================================================================
// TOP STATS
// ======================================================================
function TopStatsSection({ topStats = [] }) {
  // Always declared first so hooks order stays stable across renders
  const [bgVideoId, setBgVideoId] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/sport/videos").then((r) => {
      if (!alive) return;
      const list = r.data?.videos || r.data?.items || [];
      const first = list[0];
      const id = first?.id || first?.video_id;
      if (id) setBgVideoId(id);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!topStats.length) return null;
  return (
    <section className="relative overflow-hidden py-20" style={{ background: BG_DARK }}>
      {/* Static fallback image (visible while iframe loads or if blocked) */}
      <div
        className="absolute inset-0 pointer-events-none bg-cover bg-center"
        style={{ backgroundImage: "url('/api/sport/asset?url=https%3A%2F%2Fistreshandball.com%2Fwp-content%2Fuploads%2F2026%2F02%2F0J5A1912-scaled-e1770806108734.jpg')" }}
        aria-hidden="true"
      />
      {/* YouTube background video — autoplay, mute, loop, embeddable IPHB feed */}
      {bgVideoId && (
        <div className="absolute inset-0 pointer-events-none">
          <iframe
            key={bgVideoId}
            src={`https://www.youtube.com/embed/${bgVideoId}?autoplay=1&mute=1&controls=0&loop=1&playlist=${bgVideoId}&modestbranding=1&showinfo=0&rel=0&iv_load_policy=3&playsinline=1&disablekb=1&fs=0&cc_load_policy=0`}
            title="IPHB highlight background"
            frameBorder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen={false}
            tabIndex={-1}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            style={{ width: "120vw", height: "67.5vw", minWidth: "100%", minHeight: "100%", pointerEvents: "none" }}
            aria-hidden="true"
            data-testid="topstats-bg-video"
          />
        </div>
      )}
      {/* Dark gradient overlay so the foreground stays readable */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: `linear-gradient(135deg, rgba(22,7,50,0.92) 0%, rgba(79,27,124,0.85) 50%, rgba(22,7,50,0.95) 100%)`,
      }}/>
      {/* Pink accent glow */}
      <div className="absolute inset-0 pointer-events-none opacity-50" style={{
        background: `radial-gradient(ellipse at 80% 20%, rgba(236,31,124,0.25), transparent 50%), radial-gradient(ellipse at 20% 80%, rgba(245,166,35,0.15), transparent 50%)`,
      }}/>

      {/* HUGE LNH watermark logo behind the title */}
      <img
        src="https://www.lnh.fr/build/imgs/logo-lnh.png"
        alt=""
        aria-hidden="true"
        className="absolute left-1/2 top-[25%] -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none"
        style={{ width: "min(70vw, 600px)", opacity: 0.06, filter: "brightness(0) invert(1)" }}
        onError={(e) => {
          // Fallback to a public LNH logo if the build URL is gone
          e.currentTarget.src = "https://upload.wikimedia.org/wikipedia/fr/thumb/3/30/Logo_LNH_2022.svg/1200px-Logo_LNH_2022.svg.png";
        }}
      />

      <div className="relative max-w-5xl mx-auto px-4 lg:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1.5 rounded-full bg-pink-500/15 border border-pink-400/30 text-pink-300 font-blackhawk tracking-[0.3em] text-xs uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse"/>
            Performances officielles
          </div>
          <h2 className="font-blackhawk text-4xl sm:text-6xl uppercase italic text-white drop-shadow-[0_4px_18px_rgba(236,31,124,0.5)]">
            Top <span style={{ color: SECONDARY }}>Stats</span> <LnhBadge size="lg" className="align-middle ml-1"/>
          </h2>
          <p className="text-white/60 text-sm mt-3">Les leaders d'Istres dans les classements de la Liqui Moly StarLigue</p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {topStats.map((st, i) => (
            <motion.div
              key={i}
              variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="relative rounded-3xl overflow-hidden shadow-2xl group hover:-translate-y-1 transition backdrop-blur-md bg-white/5 border border-white/15"
            >
              <div className="absolute inset-0 opacity-30" style={{ background: `linear-gradient(135deg, ${PRIMARY}88, ${BG_DEEP}88)` }}/>
              <div className="absolute inset-0 opacity-25" style={{ background: "radial-gradient(circle at 80% 20%, rgba(245,166,35,0.5), transparent 60%)" }}/>
              <div className="relative px-6 py-8 flex items-center gap-6">
                <div className="relative shrink-0">
                  <div className="absolute -inset-1 rounded-full blur-lg opacity-60" style={{ background: SECONDARY }}/>
                  <div className="relative w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden ring-4 ring-pink-500 bg-white">
                    {st.photo_url ? <img src={st.photo_url} alt="" className="w-full h-full object-cover object-top"/> : <Star className="w-full h-full p-6 text-slate-300"/>}
                  </div>
                </div>
                <div className="flex-1 text-white">
                  <div className="flex items-center gap-2 mb-1">
                    <Flame size={14} className="text-pink-400"/>
                    <span className="text-pink-300 font-black uppercase text-[11px] tracking-[0.2em]">{st.role}</span>
                  </div>
                  <div className="font-black text-xl sm:text-2xl leading-tight uppercase">{st.name}</div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span className="text-white/60 text-xs uppercase tracking-widest inline-flex items-center gap-1.5">Score <LnhBadge size="sm"/></span>
                    <span className="font-bebas font-bold text-4xl text-pink-400">{st.score}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ======================================================================
// UPCOMING CAROUSEL
// ======================================================================
function UpcomingCarousel({ matches = [], settings }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: "start", containScroll: "trimSnaps" });
  if (!matches.length) return null;
  const ourCode = ourTeamCode(settings);
  return (
    <section className="relative pt-20 pb-24" style={{ background: BG_DARK }}>
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: "radial-gradient(ellipse at top, rgba(245,166,35,0.15), transparent 60%)" }}/>
      <div className="relative max-w-6xl mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-pink-400 font-black tracking-[0.3em] text-xs uppercase mb-2">À venir</div>
            <h2 className="text-white font-black text-3xl sm:text-4xl uppercase italic">
              Les prochains <span style={{ color: SECONDARY }}>matchs</span>
            </h2>
          </div>
          <div className="flex gap-2">
            <button onClick={() => emblaApi?.scrollPrev()} className="w-11 h-11 rounded-full bg-white/10 hover:bg-pink-500 hover:text-black text-white transition flex items-center justify-center"><ChevronLeft size={18}/></button>
            <button onClick={() => emblaApi?.scrollNext()} className="w-11 h-11 rounded-full bg-white/10 hover:bg-pink-500 hover:text-black text-white transition flex items-center justify-center"><ChevronRight size={18}/></button>
          </div>
        </div>
        <div className="overflow-hidden" ref={emblaRef}>
          <div className="flex gap-4">
            {matches.map((m, i) => {
              const homeIsUs = sameTeam(m.home_code, ourCode);
              return (
                <div key={i} className="flex-[0_0_85%] sm:flex-[0_0_45%] lg:flex-[0_0_31%]">
                  <div className="rounded-2xl bg-white/5 backdrop-blur border border-white/10 hover:border-pink-500/50 transition overflow-hidden h-full">
                    <div className="px-4 py-2 bg-pink-500/10 border-b border-white/10 flex justify-between items-center">
                      <span className="text-pink-300 text-[11px] font-black uppercase tracking-widest">{formatDate(m.date_iso)}</span>
                      <span className="text-white/70 text-[11px] font-bold">{m.time || ""}</span>
                    </div>
                    <div className="p-5 flex items-center justify-between gap-3">
                      <div className="flex-1 text-center">
                        <ClubLink code={m.home_code} name={m.home_team} ourCode={ourCode}
                          className="w-14 h-14 mx-auto rounded-full bg-white p-1.5 ring-2 ring-white/20 flex items-center justify-center">
                          <img src={m.home_logo} alt="" className="w-full h-full object-contain"/>
                        </ClubLink>
                        <ClubLink code={m.home_code} name={m.home_team} ourCode={ourCode}
                          className={`mt-2 block text-xs font-bold uppercase ${homeIsUs ? "text-pink-400" : "text-white hover:text-pink-300"}`}>
                          {m.home_team}
                        </ClubLink>
                      </div>
                      <span className="font-blackhawk text-6xl italic tracking-widest" style={{ color: SECONDARY }}>VS</span>
                      <div className="flex-1 text-center">
                        <ClubLink code={m.away_code} name={m.away_team} ourCode={ourCode}
                          className="w-14 h-14 mx-auto rounded-full bg-white p-1.5 ring-2 ring-white/20 flex items-center justify-center">
                          <img src={m.away_logo} alt="" className="w-full h-full object-contain"/>
                        </ClubLink>
                        <ClubLink code={m.away_code} name={m.away_team} ourCode={ourCode}
                          className={`mt-2 block text-xs font-bold uppercase ${!homeIsUs ? "text-pink-400" : "text-white hover:text-pink-300"}`}>
                          {m.away_team}
                        </ClubLink>
                      </div>
                    </div>
                    <div className="px-4 py-3 border-t border-white/10 text-center text-[10px] text-white/60 uppercase tracking-widest">{m.league || "StarLigue"}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ======================================================================
// NEWSLETTER + SHOP
// ======================================================================
function NewsletterShopSection({ settings }) {
  const [email, setEmail] = useState("");

  return (
    <section className="relative py-20" style={{ background: BG_DARK }}>
      <div className="max-w-6xl mx-auto px-4 lg:px-8 grid md:grid-cols-2 gap-6">
        {/* Newsletter */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="relative rounded-3xl p-8 sm:p-10 overflow-hidden shadow-2xl"
          style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
          <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full opacity-20" style={{ background: SECONDARY }}/>
          <div className="relative">
            <div className="text-pink-400 font-black uppercase tracking-[0.3em] text-[11px] mb-3">Restez connectés</div>
            <h3 className="text-white font-black text-2xl sm:text-3xl uppercase italic leading-tight">Newsletter du club</h3>
            <p className="text-white/70 mt-3 text-sm">Recevez les news exclusives, l'agenda des matchs et les offres billetterie en avant-première.</p>
            <form className="mt-6 flex gap-2" onSubmit={(e) => { e.preventDefault(); alert("Inscription enregistrée !"); }}>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.fr"
                data-testid="home-newsletter-input"
                className="flex-1 px-4 py-3 rounded-full bg-white/10 text-white placeholder-white/40 border border-white/20 focus:border-pink-500 focus:outline-none"/>
              <button data-testid="home-newsletter-submit" className="px-5 py-3 rounded-full font-black uppercase text-xs tracking-widest"
                style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)`, color: BG_DARK }}>
                <Mail size={16} className="inline"/>
              </button>
            </form>
          </div>
        </motion.div>

        {/* Shop — internal boutique */}
        <motion.div variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="relative rounded-3xl p-8 sm:p-10 overflow-hidden shadow-2xl group"
          style={{ background: `linear-gradient(135deg, ${SECONDARY}, #d4731a)` }}>
          <div className="absolute -left-12 -bottom-12 w-48 h-48 rounded-full opacity-30 bg-white"/>
          <div className="relative">
            <div className="text-[#1a0820] font-black uppercase tracking-[0.3em] text-[11px] mb-3">Boutique officielle</div>
            <h3 className="text-[#1a0512] font-black text-2xl sm:text-3xl uppercase italic leading-tight">Personnalisez votre maillot officiel</h3>
            <p className="text-[#1a0512]/80 mt-3 text-sm font-medium">Personnalisez votre nom et votre numéro, prévisualisez en direct, recevez le maillot officiel à domicile.</p>
            <Link to="/sport/boutique" data-testid="home-boutique-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#1a0820] text-white font-black uppercase text-xs tracking-widest hover:scale-105 transition">
              <ShoppingBag size={14}/> Personnaliser mon maillot <ArrowRight size={14}/>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ======================================================================
// BULL PARALLAX BANNER — taureau qui glisse au scroll
// ======================================================================
function BullParallaxBanner() {
  return (
    <section className="relative py-20 sm:py-28 overflow-hidden" style={{ background: BG_DEEP }}>
      {/* halftone */}
      <div className="absolute inset-0 opacity-15 pointer-events-none" style={{
        backgroundImage: `radial-gradient(circle, ${SECONDARY} 1px, transparent 1px)`,
        backgroundSize: "16px 16px",
        maskImage: "radial-gradient(ellipse at center, black, transparent 75%)",
      }}/>
      {/* PARALLAX BULL — slides ltr while scrolling */}
      <ParallaxBull direction="ltr" height={420} opacity={0.18}/>
      <div className="relative z-[2] max-w-5xl mx-auto px-4 lg:px-8 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="text-pink-400 font-black tracking-[0.4em] text-xs uppercase mb-3">
          #IstresTerreDeHandball
        </motion.div>
        <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
          className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2.25rem, 5vw, 4.5rem)", lineHeight: 0.95 }}>
          Une <span style={{ color: SECONDARY }}>terre</span>, un <span style={{ color: SECONDARY }}>club</span>,<br/>une légende
        </motion.h2>
        <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.25 }}
          className="mt-6 text-white/80 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
          Depuis 1970, l'Istres Provence Handball incarne la fougue, l'audace et l'esprit camarguais.
          Rejoignez la tribu et portez les couleurs avec nous.
        </motion.p>
      </div>
    </section>
  );
}

// ======================================================================
// VIDEOS SECTION (YouTube RSS)
// ======================================================================
function VideosSection() {
  const [videos, setVideos] = useState([]);
  const [activeId, setActiveId] = useState(null);
  useEffect(() => {
    api.get("/sport/videos?limit=12").then((r) => setVideos(r.data?.videos || [])).catch(() => {});
  }, []);
  if (!videos.length) return null;
  // Vidéo "à la une" = activeId si l'utilisateur a cliqué, sinon la 1re du feed
  const featured = videos.find((v) => v.id === activeId) || videos[0];
  const others = videos.filter((v) => v.id !== featured.id).slice(0, 6);
  const isPlaying = !!activeId;

  return (
    <section className="relative py-20 overflow-hidden" style={{ background: BG_DEEP }}>
      <div className="absolute inset-0 opacity-30 pointer-events-none" style={{ background: `radial-gradient(ellipse at top right, ${PRIMARY}, transparent 60%)` }}/>
      <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="text-pink-400 font-black tracking-[0.3em] text-xs uppercase mb-2 flex items-center gap-2">
              <Play size={12} fill="currentColor"/> Médiathèque
            </div>
            <h2 className="text-white font-black text-3xl sm:text-4xl uppercase italic">
              Notre <span style={{ color: SECONDARY }}>chaîne YouTube</span>
            </h2>
          </div>
          <Link to="/sport/medias" data-testid="home-videos-all"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/15 hover:bg-pink-500 hover:border-pink-500 text-white font-black uppercase text-[11px] tracking-widest transition">
            Toutes les vidéos <ArrowRight size={12}/>
          </Link>
        </div>

        {/* Pro layout: huge featured 70%, scrollable list 30% */}
        <div className="grid lg:grid-cols-[1.9fr,1fr] gap-6">
          {/* Featured (big) — joue inline dans une iframe YouTube quand cliquée */}
          {featured && (
            isPlaying ? (
              <div data-testid="featured-video-player" className="relative rounded-3xl overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.6)] block aspect-video bg-black">
                <iframe
                  key={featured.id}
                  src={`https://www.youtube.com/embed/${featured.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                  title={featured.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                />
                <button
                  type="button"
                  onClick={() => setActiveId(null)}
                  data-testid="featured-video-close"
                  className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 hover:bg-pink-600 text-white text-[10px] font-black uppercase tracking-widest transition border border-white/15"
                  aria-label="Fermer la vidéo"
                >
                  <X size={12}/> Fermer
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setActiveId(featured.id)}
                data-testid="featured-video"
                className="group relative rounded-3xl overflow-hidden shadow-[0_25px_60px_rgba(0,0,0,0.6)] block aspect-video text-left"
              >
                <img src={featured.thumbnail_hd} alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition duration-700"
                  onError={(e) => { e.currentTarget.src = featured.thumbnail; }}/>
                <div className="absolute inset-0" style={{
                  background: `linear-gradient(0deg, rgba(10,4,16,0.96) 0%, rgba(10,4,16,0.55) 35%, transparent 70%)`,
                }}/>
                <div className="absolute top-5 left-5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-[10px] font-black uppercase tracking-widest shadow-2xl"
                  style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                  <Play size={11} fill="white"/> À la une
                </div>
                {/* Pulse ring + play */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <span className="absolute inset-0 rounded-full animate-ping" style={{ background: `${SECONDARY}55` }}/>
                    <div className="relative w-24 h-24 rounded-full text-white flex items-center justify-center shadow-2xl group-hover:scale-110 transition"
                      style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                      <Play size={38} fill="white" className="ml-1.5"/>
                    </div>
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
                  <div className="text-pink-300 text-[11px] font-black uppercase tracking-widest mb-2 flex items-center gap-2">
                    {featured.channel || "Istres Provence Handball"}
                    {featured.published_at && (
                      <>
                        <span className="opacity-50">·</span>
                        <span className="text-white/70">{new Date(featured.published_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}</span>
                      </>
                    )}
                  </div>
                  <h3 className="text-white font-black text-2xl sm:text-3xl lg:text-4xl leading-tight line-clamp-2">{featured.title}</h3>
                </div>
              </button>
            )
          )}

          {/* Compact playlist (right) */}
          <div className="rounded-3xl bg-white/5 border border-white/10 overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="text-pink-400 text-[10px] font-black tracking-widest uppercase">Playlist · {others.length} vidéos</div>
              <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-red-400">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"/> Live
              </span>
            </div>
            <div className="divide-y divide-white/5 flex-1 max-h-[480px] overflow-y-auto">
              {others.map((v, i) => (
                <button key={v.id || i} type="button"
                  onClick={() => {
                    setActiveId(v.id);
                    // Scroll en haut de la section pour rendre le lecteur visible
                    if (typeof window !== "undefined") {
                      const el = document.querySelector('[data-testid="featured-video-player"], [data-testid="featured-video"]');
                      el?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }
                  }}
                  data-testid={`playlist-video-${i}`}
                  className="group w-full flex gap-3 p-3 hover:bg-pink-500/10 transition text-left">
                  <div className="relative w-32 sm:w-36 aspect-video rounded-lg overflow-hidden shrink-0 bg-black/40">
                    <img src={v.thumbnail} alt={v.title} loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition"/>
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/0 transition"/>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
                      <div className="w-9 h-9 rounded-full text-white flex items-center justify-center shadow-lg"
                        style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                        <Play size={13} fill="white" className="ml-0.5"/>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 py-0.5">
                    <h4 className="text-white text-[12.5px] font-bold leading-snug line-clamp-2 group-hover:text-pink-300 transition">{v.title}</h4>
                    <div className="text-white/50 text-[10px] mt-1.5 flex items-center gap-1.5">
                      <span>{v.channel || "IPHB"}</span>
                      {v.published_at && (
                        <>
                          <span>·</span>
                          <span>{new Date(v.published_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}</span>
                        </>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <Link to="/sport/medias" className="block text-center px-4 py-3 text-pink-400 hover:text-pink-300 hover:bg-white/5 font-black text-[10px] uppercase tracking-widest border-t border-white/10 transition">
              Voir toute la médiathèque →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

// ======================================================================
// PARTNERS CAROUSEL
// ======================================================================
function PartnersSection({ partners = [] }) {
  if (!partners.length) return null;
  // Duplicate for infinite scroll effect
  const loop = [...partners, ...partners];
  return (
    <section id="partners" className="relative bg-white py-16 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 lg:px-8 text-center mb-10">
        <div className="text-pink-600 font-black tracking-[0.3em] text-xs uppercase mb-2">Ils nous soutiennent</div>
        <h2 className="font-black text-3xl sm:text-4xl uppercase italic" style={{ color: PRIMARY }}>
          Nos <span style={{ color: SECONDARY }}>partenaires</span>
        </h2>
      </div>
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"/>
        <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"/>
        <motion.div
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 35, ease: "linear", repeat: Infinity }}
          className="flex gap-12 items-center w-max"
        >
          {loop.map((p, i) => (
            <a key={i} href={p.website_url || "#"} target={p.website_url ? "_blank" : "_self"} rel="noopener noreferrer"
              className="shrink-0 grayscale hover:grayscale-0 opacity-70 hover:opacity-100 transition-all">
              <img src={p.logo_url} alt={p.name} className="h-16 sm:h-20 max-w-[180px] object-contain"/>
            </a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ======================================================================
// FOOTER
// ======================================================================
// ======================================================================
// FOOTER HIGHLIGHTS — quick-access band above footer (équipes, business, halle)
// ======================================================================
const HALLE_PHOTO = "https://customer-assets.emergentagent.com/job_d6b30d32-0560-4729-9096-ffa84a0b3e19/artifacts/zwtichlk_image.png";

function FooterHighlightsBar({ teamInfo }) {
  const venues = teamInfo?.venues || [];
  const hallMatch = venues.find((v) => /halle/i.test(v?.name || "")) || venues[1] || venues[0];
  const matchAddr = (hallMatch?.address || "Place Champollion 13800 Istres").trim();
  const matchName = hallMatch?.name || "Halle Polyvalente";

  const tiles = [
    {
      key: "teams",
      title: "Nos équipes",
      subtitle: "Pro · Féminines · Formation · Amateur",
      icon: UsersIcon,
      to: "/sport/effectif",
      cta: "Voir l'effectif",
      gradient: `linear-gradient(135deg, ${SECONDARY} 0%, #ff7ab1 100%)`,
      links: [
        { label: "Équipe Pro", to: "/sport/effectif", icon: Trophy },
        { label: "Féminines", to: "/sport/club/feminines", icon: Heart },
        { label: "Centre de formation", to: "/sport/club/centre-formation", icon: GraduationCap },
        { label: "Secteur amateur", to: "/sport/club/amateur", icon: HandHeart },
      ],
    },
    {
      key: "business",
      title: "Espace Business",
      subtitle: "Devenez partenaire du club",
      icon: Briefcase,
      to: "/sport/partenaires",
      cta: "Découvrir les offres",
      gradient: `linear-gradient(135deg, #FFB341 0%, #FF7E5F 100%)`,
      links: [
        { label: "Hospitalité", to: "/sport/partenaires#prestations", icon: Trophy },
        { label: "Affichage maillot", to: "/sport/partenaires#prestations", icon: Megaphone },
        { label: "Annuaire partenaires", to: "/sport/partenaires#annuaire", icon: UsersIcon },
        { label: "Plaquette PDF", to: "/sport/partenaires", icon: ArrowRight },
      ],
    },
  ];

  return (
    <section
      className="relative py-12 sm:py-16 overflow-hidden"
      style={{ background: `linear-gradient(180deg, ${BG_DARK} 0%, ${BG_DEEP} 100%)` }}
      data-testid="footer-highlights-bar"
    >
      <div className="absolute inset-0 pointer-events-none opacity-40" style={{
        background: `radial-gradient(ellipse at 0% 50%, rgba(236,31,124,0.18), transparent 55%), radial-gradient(ellipse at 100% 50%, rgba(79,27,124,0.4), transparent 60%)`,
      }}/>
      <div className="relative max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center gap-3 mb-8">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-pink-400/40"/>
          <span className="font-blackhawk italic uppercase tracking-[0.3em] text-pink-300 text-sm sm:text-lg whitespace-nowrap">
            #IPHB · Bienvenue chez les Taureaux
          </span>
          <span className="h-px flex-1 bg-gradient-to-l from-transparent via-white/20 to-pink-400/40"/>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          {/* HALLE POLYVALENTE — large featured tile with real photo */}
          <a
            href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(matchName + " " + matchAddr)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-pink-400/60 transition shadow-2xl group lg:row-span-1"
            data-testid="footer-highlight-halle"
          >
            <div className="relative aspect-[16/10] lg:aspect-auto lg:h-full min-h-[320px] overflow-hidden">
              <img src={HALLE_PHOTO} alt={matchName} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>
              <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(22,7,50,0.25) 0%, rgba(22,7,50,0.95) 100%)" }}/>
              {/* Tag — top-left to avoid overlapping the bottom title */}
              <span className="absolute top-3 left-3 px-3 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest bg-pink-500 text-white shadow-lg z-10">
                Jours de match
              </span>
              {/* Bottom content */}
              <div className="absolute inset-x-0 bottom-0 p-5">
                <div className="flex items-center gap-2 text-pink-300 text-[11px] font-black uppercase tracking-widest mb-2">
                  <Trophy size={12}/> Stade officiel · Liqui Moly StarLigue
                </div>
                <div className="text-white font-blackhawk italic uppercase text-3xl sm:text-4xl leading-none">{matchName}</div>
                <div className="mt-2 flex items-start gap-2 text-white/80 text-sm">
                  <MapPin size={14} className="text-pink-400 shrink-0 mt-0.5"/>
                  <span>{matchAddr}</span>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 text-white text-xs font-black uppercase tracking-widest" data-testid="footer-highlight-halle-cta">
                  <Navigation size={13}/> Itinéraire Google Maps <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform"/>
                </div>
              </div>
            </div>
          </a>

          {/* OTHER TILES */}
          {tiles.map((t) => (
            <motion.div
              key={t.key}
              whileHover={{ y: -4 }}
              className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-pink-400/50 transition shadow-2xl group"
              style={{ background: BG_DEEP }}
              data-testid={`footer-highlight-${t.key}`}
            >
              <div className="relative min-h-[112px] sm:min-h-[120px] flex items-end p-4" style={{ background: t.gradient }}>
                <div className="absolute inset-0 opacity-20" style={{
                  background: "url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2280%22 height=%2280%22><circle cx=%221%22 cy=%221%22 r=%221%22 fill=%22%23ffffff%22/></svg>')",
                }}/>
                <div className="relative flex items-center gap-3">
                  <span className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center border border-white/30 shrink-0">
                    <t.icon size={22} className="text-white"/>
                  </span>
                  <div className="min-w-0">
                    <div className="text-white font-blackhawk italic uppercase text-2xl sm:text-3xl leading-none">{t.title}</div>
                    <div className="text-white/85 text-[11px] font-bold uppercase tracking-widest mt-1.5">{t.subtitle}</div>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-5">
                <ul className="space-y-2">
                  {t.links.map((l) => (
                    <li key={l.label}>
                      <Link to={l.to} className="flex items-center gap-2 text-white/80 hover:text-pink-300 transition text-sm group/link">
                        <l.icon size={13} className="text-pink-400 shrink-0"/>
                        <span className="flex-1">{l.label}</span>
                        <ArrowRight size={12} className="opacity-0 group-hover/link:opacity-100 group-hover/link:translate-x-0.5 transition"/>
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="mt-4">
                  <Link to={t.to} className="inline-flex items-center gap-2 text-pink-300 hover:text-white text-xs font-black uppercase tracking-widest transition" data-testid={`footer-highlight-${t.key}-cta`}>
                    {t.cta} <ArrowRight size={12}/>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ======================================================================
// PARTNERS MARQUEE — slim ticker of all partner logos
// ======================================================================
function PartnersMarqueeBar() {
  const [partners, setPartners] = useState([]);
  useEffect(() => {
    api.get("/sport/partners").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setPartners(list.filter((p) => p.logo_url));
    }).catch(() => setPartners([]));
  }, []);
  if (!partners.length) return null;
  // Triple the array so the marquee always has enough content for an infinite loop
  const loop = [...partners, ...partners, ...partners];
  // Route every logo through our backend asset proxy to bypass cross-origin /
  // referer / hotlink-protection issues from istreshandball.com.
  const proxied = (u) => {
    if (!u) return "";
    if (u.startsWith("/")) return u;
    return `${API}/sport/asset?url=${encodeURIComponent(u)}`;
  };
  return (
    <section
      className="relative overflow-hidden border-y border-white/10"
      style={{ background: `linear-gradient(90deg, ${BG_DARK} 0%, ${PRIMARY} 50%, ${BG_DARK} 100%)` }}
      data-testid="partners-marquee-bar"
    >
      {/* Subtle pink accent */}
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
        background: `radial-gradient(ellipse at 50% 0%, rgba(236,31,124,0.25), transparent 70%)`,
      }}/>

      {/* Title sticker */}
      <div className="relative max-w-7xl mx-auto px-4 lg:px-8 pt-7 pb-2 flex items-center gap-3">
        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent"/>
        <span className="font-blackhawk italic uppercase tracking-[0.3em] text-pink-300 text-xs sm:text-sm whitespace-nowrap">
          Ils nous accompagnent
        </span>
        <span className="h-px flex-1 bg-gradient-to-r from-transparent via-white/20 to-transparent"/>
      </div>

      {/* Marquee — CSS animation pour boucler en continu sans pause */}
      <div className="relative pb-7 pt-3 overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
             style={{ background: `linear-gradient(90deg, ${BG_DARK}, transparent)` }}/>
        <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-24 z-10 pointer-events-none"
             style={{ background: `linear-gradient(-90deg, ${BG_DARK}, transparent)` }}/>
        <div
          className="flex gap-10 sm:gap-16 items-center w-max iphb-marquee-track"
          style={{ animationDuration: `${Math.max(30, partners.length * 3)}s` }}
          data-testid="partners-marquee-track"
        >
          {loop.map((p, i) => (
            <a
              key={`${p.id || p.name}-${i}`}
              href={p.website_url || "#"}
              target={p.website_url ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className="shrink-0 group relative inline-flex items-center justify-center rounded-xl bg-white/95 ring-1 ring-white/20 px-3 py-2 sm:px-4 sm:py-3 shadow-[0_8px_24px_rgba(0,0,0,0.35)] transition hover:bg-white hover:scale-110 hover:ring-pink-300"
              title={p.name}
              data-testid={`partner-logo-${(p.name || "").replace(/\s/g, "-").toLowerCase()}`}
            >
              <img
                src={proxied(p.logo_url)}
                alt={p.name}
                className="h-7 sm:h-10 max-w-[120px] sm:max-w-[160px] object-contain"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
              />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function SportFooter({ settings, teamInfo }) {
  const links = teamInfo?.links || {};
  const venues = teamInfo?.venues || [];
  const year = new Date().getFullYear();
  return (
    <>
      <FooterHighlightsBar teamInfo={teamInfo}/>
      <PartnersMarqueeBar/>
      <footer className="relative pt-16 sm:pt-20 pb-24 sm:pb-10 text-white overflow-hidden" style={{ background: BG_DEEP }} data-testid="sport-footer">
        {/* Ambient glows */}
        <div className="absolute inset-0 pointer-events-none opacity-50" style={{
          background: `radial-gradient(ellipse at 90% 0%, rgba(236,31,124,0.18), transparent 50%), radial-gradient(ellipse at 10% 100%, rgba(79,27,124,0.4), transparent 60%)`,
        }}/>
        {/* Grain noise */}
        <div className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22120%22 height=%22120%22><circle cx=%221%22 cy=%221%22 r=%221%22 fill=%22%23ffffff%22/></svg>')]"/>

        <div className="relative max-w-6xl mx-auto px-5 lg:px-8 grid md:grid-cols-12 gap-8 lg:gap-12">
          {/* COLUMN 1 — Brand + social */}
          <div className="md:col-span-4">
            <Link to="/sport" className="flex items-center gap-3 group">
              <span className="relative">
                <span className="absolute -inset-1 rounded-full bg-pink-500/30 blur-md opacity-0 group-hover:opacity-100 transition"/>
                <img src={settings?.logo_url} alt="" className="relative w-16 h-16 rounded-full bg-white p-1 shadow-xl"/>
              </span>
              <div>
                <div className="font-blackhawk italic text-5xl sm:text-6xl leading-none uppercase tracking-tight">IPHB</div>
                <div className="text-pink-400 text-[11px] font-black tracking-widest uppercase mt-2">Saison {settings?.season || "2025-2026"}</div>
              </div>
            </Link>
            <p className="mt-5 text-white/70 text-sm leading-relaxed">
              <strong className="text-white">{settings?.club_name || "Istres Provence Handball"}</strong> évolue en <span className="text-pink-300 font-bold">Liqui Moly StarLigue</span> — l'élite du handball français — depuis 2007.
            </p>
            <SocialLinks links={links} settings={settings} className="mt-6 flex gap-2 flex-wrap"/>

            {/* Newsletter */}
            <form
              className="mt-7 flex"
              onSubmit={(e) => { e.preventDefault(); const v=e.currentTarget.elements.email.value; if (v) { try { window.localStorage.setItem("iphb_newsletter", v); } catch(_){} alert("Merci pour votre inscription à la newsletter IPHB ! 📬"); e.currentTarget.reset(); } }}
              data-testid="footer-newsletter"
            >
              <input
                type="email" name="email" required placeholder="Votre email"
                className="flex-1 min-w-0 bg-white/5 border border-white/15 focus:border-pink-400 focus:bg-white/10 rounded-l-full px-4 py-2.5 text-sm placeholder-white/40 outline-none transition"
              />
              <button type="submit" className="rounded-r-full px-5 py-2.5 font-black uppercase text-xs tracking-widest text-white shadow-lg hover:scale-[1.02] transition" style={{ background: `linear-gradient(135deg, ${SECONDARY}, #ff3a8c)` }}>
                S'inscrire
              </button>
            </form>
            <p className="text-[10px] text-white/40 mt-2">News du club, billets, offres partenaires. Désinscription en 1 clic.</p>
          </div>

          {/* COLUMN 2 — Le Club */}
          <div className="md:col-span-2">
            <div className="text-pink-400 font-blackhawk italic tracking-[0.15em] uppercase text-xl sm:text-2xl mb-4">Le Club</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><Link to="/sport/club/histoire" className="hover:text-pink-300 transition">Présentation</Link></li>
              <li><Link to="/sport/club/salle" className="hover:text-pink-300 transition">La Halle</Link></li>
              <li><Link to="/sport/club/staff" className="hover:text-pink-300 transition">Le staff</Link></li>
              <li><Link to="/sport/effectif" className="hover:text-pink-300 transition">L'effectif</Link></li>
              <li><Link to="/sport/calendrier" className="hover:text-pink-300 transition">Calendrier</Link></li>
              <li><Link to="/sport/classement" className="hover:text-pink-300 transition">Classement</Link></li>
            </ul>
          </div>

          {/* COLUMN 3 — Pratique */}
          <div className="md:col-span-2">
            <div className="text-pink-400 font-blackhawk italic tracking-[0.15em] uppercase text-xl sm:text-2xl mb-4">Pratique</div>
            <ul className="space-y-2.5 text-sm text-white/70">
              <li><a href={settings?.ticketing_url || "https://billetterie.istreshandball.com/"} target="_blank" rel="noopener noreferrer" className="hover:text-pink-300 transition">Billetterie</a></li>
              <li><Link to="/sport/boutique" className="hover:text-pink-300 transition">Boutique officielle</Link></li>
              <li><Link to="/sport/partenaires" className="hover:text-pink-300 transition">Devenir partenaire</Link></li>
              <li><Link to="/sport/actualites" className="hover:text-pink-300 transition">Actualités</Link></li>
              <li><Link to="/sport/mon-espace" className="hover:text-pink-300 transition">Mon Espace</Link></li>
              <li><Link to="/sport/contact" className="hover:text-pink-300 transition">Contact</Link></li>
            </ul>
          </div>

          {/* COLUMN 4 — Contact + Salle */}
          <div className="md:col-span-4">
            <div className="text-pink-400 font-blackhawk italic tracking-[0.15em] uppercase text-xl sm:text-2xl mb-4">Nous trouver</div>

            {/* BUREAU — small / discreet block at the top */}
            <div className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 mb-4">
              <div className="text-white/40 text-[9px] font-black uppercase tracking-widest mb-1.5">Bureau du club · Administration</div>
              <div className="space-y-1.5 text-[12px] text-white/65">
                {teamInfo?.address && (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(teamInfo.address)}`} target="_blank" rel="noopener noreferrer"
                     className="flex items-start gap-2 hover:text-pink-300 transition">
                    <MapPin size={11} className="shrink-0 mt-0.5 text-pink-400/70"/>
                    <span className="leading-snug">{teamInfo.address}</span>
                  </a>
                )}
                {teamInfo?.phone && (
                  <a href={`tel:${teamInfo.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-pink-300 transition">
                    <Phone size={11} className="shrink-0 text-pink-400/70"/>
                    <span>{teamInfo.phone}</span>
                  </a>
                )}
                {teamInfo?.email && (
                  <a href={`mailto:${teamInfo.email}`} className="flex items-center gap-2 hover:text-pink-300 transition">
                    <Mail size={11} className="shrink-0 text-pink-400/70"/>
                    <span className="truncate">{teamInfo.email}</span>
                  </a>
                )}
              </div>
            </div>

            {/* HALLE POLYVALENTE — featured below (stadium of the club) */}
            {(() => {
              const halle = venues.find((v) => /halle/i.test(v?.name || "")) || venues[venues.length - 1] || null;
              const halleName = halle?.name || "Halle Polyvalente";
              const halleAddr = halle?.address || "Allée des Échoppes, Place Champollion — 13800 Istres";
              return (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(halleName + " " + halleAddr)}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="footer-halle-cta"
                  className="block rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl group transition border border-pink-400/30 hover:border-pink-400"
                  style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}
                >
                  <div className="relative px-4 py-4">
                    <div className="absolute top-2 right-2 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest bg-pink-500 text-white shadow-lg">
                      Stade officiel
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center shrink-0 border border-white/20">
                        <Trophy size={22} className="text-pink-300"/>
                      </span>
                      <div className="min-w-0">
                        <div className="text-pink-300 text-[10px] font-black uppercase tracking-widest">Jours de match</div>
                        <div className="font-blackhawk italic uppercase text-white text-2xl leading-none mt-1">{halleName}</div>
                        <div className="text-white/80 text-xs mt-2 leading-relaxed">{halleAddr}</div>
                        <div className="mt-2.5 inline-flex items-center gap-1.5 text-pink-200 group-hover:text-white text-[10px] font-black uppercase tracking-widest transition">
                          <MapPin size={11}/> Itinéraire <ArrowRight size={10} className="group-hover:translate-x-1 transition-transform"/>
                        </div>
                      </div>
                    </div>
                  </div>
                </a>
              );
            })()}
          </div>
        </div>

        <div className="relative max-w-6xl mx-auto mt-14 pt-6 border-t border-white/10 px-5 lg:px-8 space-y-4">
          {/* Legal links row */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/60">
            <Link to="/sport/legal/mentions" className="hover:text-pink-300 transition" data-testid="footer-legal-mentions">Mentions légales</Link>
            <span className="text-white/20">·</span>
            <Link to="/sport/legal/reglement" className="hover:text-pink-300 transition" data-testid="footer-legal-reglement">Règles du club</Link>
            <span className="text-white/20">·</span>
            <Link to="/sport/legal/confidentialite" className="hover:text-pink-300 transition" data-testid="footer-legal-privacy">Politique de confidentialité</Link>
            <span className="text-white/20">·</span>
            <Link to="/sport/legal/cgv" className="hover:text-pink-300 transition" data-testid="footer-legal-cgv">CGV Boutique</Link>
          </div>

          {/* PERFS WALL — accès direct à l'écran live diffusable (caché si désactivé) */}
          {settings?.sport_perfs_wall_enabled !== false && (
          <div className="flex items-center justify-center pt-1">
            <Link
              to="/sport/perfs-wall"
              target="_blank"
              rel="noopener"
              data-testid="footer-perfs-wall"
              className="group inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition"
              style={{
                background: "linear-gradient(135deg, #EC1F7C 0%, #ff3a8c 100%)",
                color: "#fff",
                boxShadow: "0 8px 24px -8px rgba(236,31,124,0.55)",
              }}
            >
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-70"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              PERFS WALL — Live Match
              <span className="opacity-70 group-hover:translate-x-0.5 transition">→</span>
            </Link>
          </div>
          )}

          {/* Copyright */}
          <div className="text-center text-xs text-white/40 inline-flex items-center justify-center gap-2 flex-wrap w-full">
            <span>© {year} {settings?.club_name || "Istres Provence Handball"} · Tous droits réservés · Données officielles :</span>
            <LnhBadge size="sm"/>
          </div>

          {/* Agency credit — MSC, links to lead capture page */}
          <Link to="/lancez-votre-site" data-testid="footer-agency-credit" className="block group">
            <div className="flex flex-wrap items-center justify-center gap-3 text-[11px] text-white/50 hover:text-white transition py-3">
              <span>Site et application développés par</span>
              <span className="inline-flex items-center justify-center px-3 py-1.5 rounded-md bg-white/10 group-hover:bg-white/20 border border-white/15 group-hover:border-pink-400/60 transition shadow-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_d6b30d32-0560-4729-9096-ffa84a0b3e19/artifacts/r4wano64_image.png"
                  alt="Agence MSC"
                  className="h-7 sm:h-8 w-auto object-contain"
                  style={{ filter: "brightness(0) invert(1)" }}
                  loading="lazy"
                />
              </span>
            </div>
          </Link>

          {/* Bouton accès back-office club — logo DigiPilote discret bas-droite */}
          <div className="mt-3 flex justify-center">
            <Link
              to="/admin/login"
              data-testid="footer-admin-login"
              className="group inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/30 transition opacity-50 hover:opacity-100"
              title="DigiPilote — Espace administrateur"
              aria-label="DigiPilote — Accès administrateur"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_d6b30d32-0560-4729-9096-ffa84a0b3e19/artifacts/t0fx03n8_digipilote_logo.png"
                alt="DigiPilote"
                className="w-7 h-7 object-contain transition-transform group-hover:scale-110"
                draggable="false"
              />
            </Link>
          </div>
        </div>
      </footer>
    </>
  );
}

// ======================================================================
// LAYOUT
// ======================================================================
export function SportPublicLayout({ children }) {
  const [settings, setSettings] = useState(null);
  const [teamInfo, setTeamInfo] = useState(null);
  const loc = useLocation();
  // Pages with their own full-bleed hero already accounting for the header should not receive top padding.
  const heroPages = ["/sport"];
  const hasHero = heroPages.includes(loc.pathname);
  useEffect(() => {
    api.get("/sport/club-info").then((r) => setSettings(r.data?.settings || {})).catch(() => setSettings({}));
    api.get("/sport/team-info").then((r) => setTeamInfo(r.data?.info || {})).catch(() => setTeamInfo({}));
  }, []);
  return (
    <div className="min-h-svh flex flex-col bg-white">
      <DemoModeBanner/>
      <SportHeader settings={settings || {}}/>
      <main className={`flex-1 ${hasHero ? "" : "pt-[80px]"}`}>
        {React.cloneElement(children, { __settings: settings, __teamInfo: teamInfo })}
      </main>
      <SportFooter settings={settings || {}} teamInfo={teamInfo || {}}/>
      <SportSpeedDial ticketingUrl={settings?.ticketing_url}/>
      <CartDrawer/>
      <FloatingInstallPWA/>
    </div>
  );
}

// Bannière mode démo — visible publiquement quand le club a activé le mode test
function DemoModeBanner() {
  const [active, setActive] = useState(false);
  useEffect(() => {
    let alive = true;
    const fetchStatus = () => {
      api.get("/sport/demo-mode/public")
        .then((r) => { if (alive) setActive(!!r.data?.enabled); })
        .catch(() => {});
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 30000); // re-check toutes 30s
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (!active) return null;
  return (
    <div className="sticky top-0 z-[60] bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 text-white text-center py-2 px-3 shadow-lg" data-testid="demo-mode-banner">
      <div className="flex items-center justify-center gap-2 text-xs sm:text-sm font-black uppercase tracking-wider">
        <span className="animate-pulse">🧪</span>
        <span>Mode démo actif — toute commande, pronostic ou test sera supprimé à la fin de la session</span>
        <span className="animate-pulse">🧪</span>
      </div>
    </div>
  );
}

// ======================================================================
// HOME PAGE
// ======================================================================
export function SportHomePage(props) {
  const settings = props.__settings;
  const teamInfo = props.__teamInfo;
  const [standings, setStandings] = useState([]);
  const [next, setNext] = useState(null);
  const [last, setLast] = useState(null);
  const [mvpWinner, setMvpWinner] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    api.get("/sport/standings").then((r) => setStandings(r.data?.rows || [])).catch(() => {});
    api.get("/sport/next-match").then((r) => setNext(r.data?.match || null)).catch(() => {});
    api.get("/sport/last-result").then((r) => {
      setLast(r.data?.match || null);
      setMvpWinner(r.data?.mvp_winner || null);
    }).catch(() => {});
    api.get("/sport/upcoming-matches?limit=6").then((r) => setUpcoming(r.data?.matches || [])).catch(() => {});
    api.get("/sport/partners").then((r) => setPartners(r.data?.partners || [])).catch(() => {});
  }, []);

  if (!settings) return null;
  return (
    <div>
      <HeroSection settings={settings}/>
      <PerformanceDashboardSection settings={settings} next={next} last={last} mvpWinner={mvpWinner} standings={standings}/>
      <TopStatsSection topStats={teamInfo?.top_stats || []}/>
      <UpcomingCarousel matches={upcoming} settings={settings}/>
      <BullParallaxBanner/>
      <VideosSection/>
      <NewsletterShopSection settings={settings}/>
      <PartnersSection partners={partners}/>
    </div>
  );
}

// ======================================================================
// PLAYERS PAGE — with overlay hover effect like reference site
// ======================================================================
function PlayerCard({ p }) {
  // Link to internal player detail page (synced with LNH)
  const target = p.slug ? `/sport/joueur/${p.slug}` : (p.lnh_url || "#");
  const isExternal = !p.slug;
  return (
    <Link to={isExternal ? target : target}
      data-testid={`player-card-${p.slug || ""}`}
      className="group relative block rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition focus:outline-none focus-visible:ring-4 focus-visible:ring-pink-500">
      {/* aspect 3:4 */}
      <div className="relative pb-[133%] overflow-hidden" style={{ background: `linear-gradient(180deg, ${PRIMARY} 0%, ${BG_DEEP} 100%)` }}>
        {/* decorative diagonal stripes */}
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: `repeating-linear-gradient(135deg, transparent 0 14px, rgba(255,255,255,0.04) 14px 16px)`,
        }}/>
        {/* photo */}
        {p.photo_url ? (
          <img src={p.photo_url} alt={p.name}
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentNode?.querySelector('[data-fallback-bull]')?.classList.remove('hidden'); }}
            className="absolute inset-0 w-full h-full object-cover object-top transform-gpu group-hover:scale-[1.04] transition duration-500"/>
        ) : null}
        {!p.photo_url && (
          <div className="absolute inset-0 flex items-center justify-center"
            data-testid="player-bull-fallback"
            style={{ background: `radial-gradient(circle at center, ${PRIMARY}aa 0%, ${BG_DEEP} 80%)` }}>
            <img src="/iphb-logo.png" alt="" aria-hidden="true"
              className="w-3/4 h-3/4 object-contain opacity-40"
              style={{ filter: `drop-shadow(0 0 30px ${SECONDARY}66)` }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}/>
          </div>
        )}
        {/* huge jersey number */}
        {p.jersey_number != null && (
          <div className="absolute top-3 right-4 select-none pointer-events-none">
            <div className="text-white/15 group-hover:text-pink-400/40 transition font-bebas"
              style={{ fontSize: "clamp(3.5rem, 9vw, 7rem)", lineHeight: 1, transform: "rotate(-8deg)", fontWeight: 700 }}>
              {p.jersey_number}
            </div>
          </div>
        )}
        {/* OVERLAY on hover */}
        <div className="absolute inset-x-0 bottom-0 translate-y-[68%] group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.21,0.94,0.4,1)]"
          style={{ background: `linear-gradient(0deg, ${BG_DARK} 60%, transparent 100%)` }}>
          <div className="px-4 pt-12 pb-4 text-center">
            <div className="font-black uppercase text-white text-base sm:text-lg tracking-tight leading-tight">{p.name}</div>
            <div className="text-pink-400 text-[11px] font-bold tracking-widest uppercase mt-1">{p.position}</div>
            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest opacity-0 group-hover:opacity-100 transition delay-100"
              style={{ background: SECONDARY, color: "white" }}>
              Voir la fiche <ArrowRight size={12}/>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export function SportPlayersPage(props) {
  const settings = props.__settings;
  const teamInfo = props.__teamInfo;
  const players = teamInfo?.players || [];
  const staff = teamInfo?.staff || [];

  const grouped = useMemo(() => {
    const buckets = {};
    POSITION_GROUPS.forEach((g) => { buckets[g.key] = []; });
    players.forEach((p) => {
      const k = positionBucket(p.position);
      (buckets[k] = buckets[k] || []).push(p);
    });
    return buckets;
  }, [players]);

  return (
    <div className="bg-white">
      {/* Page hero */}
      <section className="relative pt-32 pb-12 sm:pt-40 sm:pb-16 overflow-hidden" style={{ background: `linear-gradient(135deg, ${BG_DARK} 0%, ${PRIMARY} 100%)` }}>
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 80% 30%, rgba(245,166,35,0.4), transparent 60%)" }}/>
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-pink-400 font-black tracking-[0.4em] text-xs uppercase mb-3">
            Saison {settings?.season || "2025-2026"}
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 0.95 }}>
            L'effectif <span style={{ color: SECONDARY }}>professionnel</span>
          </motion.h1>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto">{players.length} joueurs · {staff.length} staff technique · Liqui Moly StarLigue</p>
        </div>
        <TornEdge position="bottom" color="#ffffff" variant="jagged" height={56}/>
      </section>

      {/* Players by position */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-16 space-y-16">
        {POSITION_GROUPS.map((g) => {
          const list = grouped[g.key] || [];
          if (!list.length) return null;
          return (
            <div key={g.key}>
              <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="flex items-center gap-3 mb-6">
                <div className="h-1 flex-1 rounded-full" style={{ background: `linear-gradient(90deg, ${PRIMARY}, transparent)` }}/>
                <h2 className="font-black uppercase text-xl sm:text-2xl tracking-tight italic" style={{ color: PRIMARY }}>
                  {g.label} <span className="text-pink-500">·</span> <span className="text-slate-400 text-sm font-medium normal-case">{list.length}</span>
                </h2>
                <div className="h-1 flex-1 rounded-full" style={{ background: `linear-gradient(-90deg, ${PRIMARY}, transparent)` }}/>
              </motion.div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {list.map((p, i) => (
                  <motion.div key={p.slug || p.name} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                    <PlayerCard p={p}/>
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Staff */}
      {staff.length > 0 && (
        <section id="staff" className="relative py-20" style={{ background: "#f5f1f7" }}>
          <div className="max-w-6xl mx-auto px-4 lg:px-8">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-10">
              <div className="text-pink-600 font-black tracking-[0.3em] text-xs uppercase mb-2">Encadrement</div>
              <h2 className="font-black text-3xl sm:text-4xl uppercase italic" style={{ color: PRIMARY }}>
                Staff <span style={{ color: SECONDARY }}>technique</span>
              </h2>
            </motion.div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
              {staff.map((s, i) => (
                <motion.div key={s.slug || i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                  <PlayerCard p={s}/>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Top stats inline (mini) */}
      {(teamInfo?.top_stats || []).length > 0 && (
        <div id="stats">
          <TopStatsSection topStats={teamInfo.top_stats}/>
        </div>
      )}
    </div>
  );
}

// ======================================================================
// CALENDAR PAGE
// ======================================================================
export function SportCalendarPage(props) {
  const settings = props.__settings;
  const [matches, setMatches] = useState([]);
  const [standings, setStandings] = useState([]);
  const [filter, setFilter] = useState("all"); // all | upcoming | finished

  useEffect(() => {
    api.get("/sport/team-calendar").then((r) => setMatches(r.data?.matches || [])).catch(() => {});
    api.get("/sport/standings").then((r) => setStandings(r.data?.rows || [])).catch(() => {});
  }, []);

  const ourCode = ourTeamCode(settings);
  const filtered = matches.filter((m) => filter === "all" ? true : filter === "finished" ? m.status === "finished" : m.status !== "finished");

  return (
    <div className="bg-white">
      <section className="relative pt-32 pb-12 sm:pt-40 sm:pb-16 overflow-hidden" style={{ background: `linear-gradient(135deg, ${BG_DARK} 0%, ${PRIMARY} 100%)` }}>
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 80% 30%, rgba(245,166,35,0.4), transparent 60%)" }}/>
        <div className="relative max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <div className="text-pink-400 font-black tracking-[0.4em] text-xs uppercase mb-3">{settings?.season || "2025-2026"}</div>
          <h1 className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2.25rem, 6vw, 4.5rem)", lineHeight: 0.95 }}>
            Saison & <span style={{ color: SECONDARY }}>Calendrier</span>
          </h1>
        </div>
        <TornEdge position="bottom" color="#ffffff" variant="jagged" height={56}/>
      </section>

      <div id="results" className="max-w-6xl mx-auto px-4 lg:px-8 py-16">
        {/* Filters */}
        <div className="flex justify-center gap-2 mb-8 flex-wrap">
          {[{ k: "all", label: "Tous les matchs" }, { k: "upcoming", label: "À venir" }, { k: "finished", label: "Résultats" }].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
              className={`px-5 py-2.5 rounded-full font-black uppercase text-xs tracking-widest transition ${
                filter === f.k ? "text-white shadow-lg" : "text-slate-600 bg-slate-100 hover:bg-slate-200"
              }`}
              style={filter === f.k ? { background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` } : {}}>
              {f.label}
            </button>
          ))}
        </div>
        {filtered.length === 0 ? (
          <div className="text-center text-slate-400 italic py-16">Aucun match dans cette catégorie.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((m, i) => {
              const finished = m.status === "finished";
              const homeIsUs = sameTeam(m.home_code, ourCode);
              const isWin = finished && (
                (homeIsUs && m.score_home > m.score_away) ||
                (!homeIsUs && m.away_code === ourCode && m.score_away > m.score_home)
              );
              return (
                <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true }}
                  className={`relative rounded-xl bg-white border ${isWin ? "border-emerald-400 shadow-emerald-100" : "border-slate-200"} hover:border-pink-500 hover:shadow-lg transition overflow-hidden`}>
                  {isWin && <ConfettiBurst count={20}/>}
                  <div className="px-3 py-2 flex justify-between items-center text-[10px] font-black uppercase tracking-widest"
                    style={{ background: finished ? "#f1eaf3" : "#fff7e6", color: PRIMARY }}>
                    <span>{formatDate(m.date_iso)} {m.time && <>· {m.time}</>}</span>
                    <span>{m.league || "StarLigue"}</span>
                  </div>
                  <div className="p-4 flex items-center justify-between gap-3 relative">
                    {!homeIsUs && m.away_code === ourCode && (
                      <div className="absolute top-1 right-1 z-10 w-7 h-7 rounded-full flex items-center justify-center text-white shadow-lg ring-2 ring-white"
                        style={{ background: "linear-gradient(135deg, #38bdf8, #2563eb)" }}>
                        <Plane size={12} className="rotate-[-30deg]"/>
                      </div>
                    )}
                    <div className="flex-1 text-center">
                      <ClubLink code={m.home_code} name={m.home_team} ourCode={ourCode}
                        className="w-12 h-12 mx-auto rounded-full bg-slate-50 ring-1 ring-slate-200 p-1.5 flex items-center justify-center">
                        <img src={m.home_logo} alt="" className="w-full h-full object-contain"/>
                      </ClubLink>
                      <ClubLink code={m.home_code} name={m.home_team} ourCode={ourCode}
                        className={`mt-1.5 block text-xs font-black uppercase ${homeIsUs ? "text-pink-600" : "text-slate-800 hover:text-pink-600"}`}>
                        {m.home_team}
                      </ClubLink>
                    </div>
                    {finished ? (
                      <div className="text-center">
                        <div className="text-2xl font-black">
                          <span className={m.score_home > m.score_away ? "text-pink-500" : "text-slate-900"}>{m.score_home}</span>
                          <span className="text-slate-300 mx-1">·</span>
                          <span className={m.score_away > m.score_home ? "text-pink-500" : "text-slate-900"}>{m.score_away}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="font-blackhawk text-5xl italic tracking-widest" style={{ color: SECONDARY }}>VS</div>
                    )}
                    <div className="flex-1 text-center">
                      <ClubLink code={m.away_code} name={m.away_team} ourCode={ourCode}
                        className="w-12 h-12 mx-auto rounded-full bg-slate-50 ring-1 ring-slate-200 p-1.5 flex items-center justify-center">
                        <img src={m.away_logo} alt="" className="w-full h-full object-contain"/>
                      </ClubLink>
                      <ClubLink code={m.away_code} name={m.away_team} ourCode={ourCode}
                        className={`mt-1.5 block text-xs font-black uppercase ${!homeIsUs && m.away_code === ourCode ? "text-pink-600" : "text-slate-800 hover:text-pink-600"}`}>
                        {m.away_team}
                      </ClubLink>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      <StandingsSection rows={standings} settings={settings || {}}/>
    </div>
  );
}

// ======================================================================
// CLUB PAGE (info / palmarès / salle / staff via slug)
// ======================================================================
export function SportClubPage(props) {
  const settings = props.__settings;
  const teamInfo = props.__teamInfo;
  const { slug } = useParams();
  const [page, setPage] = useState(null);

  useEffect(() => {
    if (!slug) return;
    api.get(`/sport/pages/${slug}`).then((r) => setPage(r.data?.page || null)).catch(() => setPage(null));
  }, [slug]);

  return (
    <div className="bg-white min-h-svh">
      <section className="relative pt-32 pb-10 sm:pt-40 sm:pb-12 overflow-hidden" style={{ background: `linear-gradient(135deg, ${BG_DARK} 0%, ${PRIMARY} 100%)` }}>
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 80% 30%, rgba(245,166,35,0.4), transparent 60%)" }}/>
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8 text-center">
          <div className="text-pink-400 font-black tracking-[0.4em] text-xs uppercase mb-3">Le club</div>
          <h1 className="text-white font-black uppercase italic" style={{ fontSize: "clamp(2rem, 5vw, 3.75rem)", lineHeight: 0.95 }}>
            {page?.title || (slug || "").replace(/-/g, " ")}
          </h1>
        </div>
        <TornEdge position="bottom" color="#ffffff" variant="rip" height={48}/>
      </section>

      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-16 grid lg:grid-cols-3 gap-12">
        <article className="lg:col-span-2 prose prose-slate max-w-none">
          {page?.body_html ? (
            <div dangerouslySetInnerHTML={{ __html: page.body_html }}/>
          ) : (
            <div className="text-slate-500 italic">Contenu en cours de rédaction.</div>
          )}
        </article>

        <aside className="space-y-6">
          {(teamInfo?.venues || []).map((v, i) => (
            <div key={i} className="rounded-2xl p-6 shadow-xl" style={{ background: `linear-gradient(135deg, ${PRIMARY}, ${BG_DEEP})` }}>
              <div className="text-pink-400 font-black uppercase tracking-widest text-[10px] mb-2">Salle</div>
              <div className="text-white font-black text-lg uppercase">{v.name}</div>
              <div className="text-white/70 text-sm mt-1">{v.address}</div>
              {v.capacity && <div className="mt-3 inline-block px-3 py-1 rounded-full bg-pink-500 text-black font-black text-xs uppercase tracking-widest">{v.capacity} places</div>}
            </div>
          ))}
          {(teamInfo?.palmares || []).length > 0 && (
            <div className="rounded-2xl p-6 bg-slate-50 border border-slate-200">
              <div className="text-pink-600 font-black uppercase tracking-widest text-[10px] mb-3">Palmarès</div>
              <ul className="space-y-2">
                {teamInfo.palmares.map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Award size={16} className="mt-0.5 text-pink-600 shrink-0"/>
                    <span><strong>{p.year}</strong> · {p.title}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

export default SportHomePage;
